import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import {
  FaArrowLeft,
  FaSpinner,
  FaPlus,
  FaFile,
  FaArchive,
  FaToggleOn,
  FaToggleOff,
  FaPaperclip,
  FaMicrophone,
  FaStop,
  FaTimes,
  FaArrowUp
} from "react-icons/fa";
import imageCompression from 'browser-image-compression';
import { FilePreviewChip } from "./FilePreviewChip";
import MobileQwen3Manager, { ModelLoadProgress } from "./MobileQwen3Manager";
import { MobileQwen3WebLLMProcessor } from './webllm_integration_azure';
import Qwen3Manager from './Qwen3Manager';
import { getLocalModelCapability, LocalModelCapability } from "./hybrid_model_selection";
import { DecryptionManager } from "./DecryptionManager";
import axios from "axios";   
import { fetchEventSource } from '@microsoft/fetch-event-source';
import "../css/chatbot.css";

// Import the centralized tool configuration
import ArchiveSidebar, { ArchiveSidebarRef } from "./ArchiveSidebar";
import { ToolConfig, UniversalMessageProps } from "./ToolConfig";

// Dictation Mode - Voice Recording
import { AudioPreviewChip } from "./AudioPreviewChip";

const API_SERVER_URL = "https://www.enlive.inc";
const STREAMING_API_SERVER_URL = "https://stream.enlive.inc";

const MAX_TASK6_LOOPS = 2;  // may increase to 3 upon Task 6 upgrade approval...
const LOCAL_TASK_DEBUG = true;

function hashStringForDebug(input: string): string {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function previewForConsole(input: string, max = 2000): string {
  const value = String(input || '');
  return value.length <= max
    ? value
    : `${value.slice(0, max)}\n\n...[truncated ${value.length - max} chars]...`;
}

function logLocalTaskPayload(label: string, payload: Record<string, any>) {
  if (!LOCAL_TASK_DEBUG) return;
  console.groupCollapsed(`Local Qwen debug: ${label}`);
  for (const [key, value] of Object.entries(payload)) {
    if (typeof value === 'string') {
      console.log(`${key}:`, {
        chars: value.length,
        hash: hashStringForDebug(value),
        preview: previewForConsole(value),
      });
    } else {
      console.log(`${key}:`, value);
    }
  }
  console.groupEnd();
}

function assertNonEmptyLocalPrompt(taskName: string, prompt: string, extra?: Record<string, any>) {
  if (typeof prompt !== 'string' || prompt.trim().length < 20) {
    console.error(`${taskName} prompt is empty or too short`, {
      chars: prompt?.length || 0,
      prompt,
      ...extra,
    });
    throw new Error(`${taskName} prompt is empty after frontend decryption/handoff; refusing to call local WebLLM.`);
  }
}

function isLowLocalDegenerateError(err: any): boolean {
  const message = String(err?.message || err || '');
  return message.includes('LOW_LOCAL_DEGENERATE_OUTPUT') || message.includes('LOW_LOCAL_HEALTH_CHECK_FAILED');
}

function blockLowLocalAfterDegenerateFailure(reason: string) {
  try {
    localStorage.setItem('qwen3LowLocalBlockedUntil', String(Date.now() + 24 * 60 * 60 * 1000));
    localStorage.setItem('qwen3LowLocalBlockedReason', reason || 'degenerate output');
  } catch {
    // Non-fatal; this is a best-effort local compatibility block.
  }
}

// ============================================================
// Props interface — now accepts a ToolConfig object
// ============================================================
export interface ChatbotToolProps {
  setSelectedTool: (tool: string | null) => void;
  className?: string;
  isDark?: boolean;
  config: ToolConfig;
}

interface APIChatMessage {
  content: string;
  role: "user" | "assistant";
}

type LoadingStage =
  | 'idle'
  | 'thinking'       // NEW
  | 'searching'
  | 'retrieving'
  | 'generating'
  | 'streaming'
  | 'scheduling'
  | 'executing'
  | 'checking_email'
  | 'cancelling'
  | 'refining_context';   // NEW: Inner loop refinement step

interface ScheduledTaskNotificationPayload {
  message?: string;
  task_name?: string;
  task_id?: number;
  status?: string;
  conversation_id?: number;
  artifacts?: any[];
  debug_payload?: any;
}

type NotificationStreamListener = (
  payload: ScheduledTaskNotificationPayload
) => void;

let sharedNotificationEventSource: EventSource | null = null;
let sharedNotificationAuth0Sub: string | null = null;
const notificationStreamListeners = new Set<NotificationStreamListener>();

function ensureSharedNotificationStream(auth0Sub: string): void {
  if (
    sharedNotificationEventSource &&
    sharedNotificationAuth0Sub === auth0Sub
  ) {
    return;
  }

  if (sharedNotificationEventSource) {
    sharedNotificationEventSource.close();
    sharedNotificationEventSource = null;
    sharedNotificationAuth0Sub = null;
  }

  const eventSource = new EventSource(
    `${STREAMING_API_SERVER_URL}/notifications-stream?auth0_sub=${encodeURIComponent(auth0Sub)}`
  );

  eventSource.onopen = () => {
    console.debug('[ScheduledTaskNotification] notifications-stream connected');
  };

  eventSource.addEventListener("notification", (event: Event) => {
    try {
      const messageEvent = event as MessageEvent;
      const payload = JSON.parse(
        messageEvent.data
      ) as ScheduledTaskNotificationPayload;

      notificationStreamListeners.forEach((listener) => listener(payload));
    } catch (error) {
      console.error("Failed to parse scheduled task notification:", error);
    }
  });

  eventSource.addEventListener("heartbeat", () => {
    // keepalive only
  });

  eventSource.onerror = (error) => {
    console.warn("notifications-stream error:", error);
  };

  sharedNotificationEventSource = eventSource;
  sharedNotificationAuth0Sub = auth0Sub;
}

function subscribeToScheduledTaskNotifications(
  auth0Sub: string,
  listener: NotificationStreamListener
) {
  notificationStreamListeners.add(listener);
  ensureSharedNotificationStream(auth0Sub);

  return () => {
    notificationStreamListeners.delete(listener);

    if (
      notificationStreamListeners.size === 0 &&
      sharedNotificationEventSource
    ) {
      sharedNotificationEventSource.close();
      sharedNotificationEventSource = null;
      sharedNotificationAuth0Sub = null;
    }
  };
}

// Helper to get a cookie from document.cookie
function getCookieValue(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function resizeTextArea(
  textArea: HTMLElement | null,
  height: number | null = null
) {
  if (textArea != null) {
    // Reset height to auto to correctly calculate the new scrollHeight
    textArea.style.height = "auto";
    
    if (height != null) {
      textArea.style.height = height + "px";
    } else {
      // Calculate max height: 30% of viewport height, capped at 200px
      const maxVh = window.innerHeight * 0.3;
      const maxHeight = Math.min(maxVh, 200); 
      
      // Apply the smaller of the actual content height or the max height
      const newHeight = Math.min(textArea.scrollHeight, maxHeight);
      textArea.style.height = newHeight + "px";
      
      // Toggle overflow based on whether we hit the cap
      if (textArea.scrollHeight > maxHeight) {
        textArea.style.overflowY = "auto";
      } else {
        textArea.style.overflowY = "hidden";
      }
    }
  }
}

// ============================================================
// Consolidated ChatbotTool component
// ============================================================
export function ChatbotTool({
  className,
  setSelectedTool,
  isDark,
  config,
}: ChatbotToolProps): ReactElement {

  // Destructure all tool-specific values from config
  const {
    toolId: TOOL_ID,
    title,
    placeholder,
    fileAccept,
    dragDropAllowedExtensions,
    InfoSidebar,
    MessageComponent,
  } = config;

  // ================================
  // State
  // ================================
  const [chatBotMessages, setChatBotMessages] = useState<
    Array<UniversalMessageProps>
  >([]);
  const [apiChatMessages, setApiChatMessages] = useState<Array<APIChatMessage>>(
    []
  );
  const notificationUnsubscribeRef = useRef<(() => void) | null>(null);
  const hasEnabledScheduledTaskNotificationsRef = useRef(false);
  const [archivedConversations, setArchivedConversations] = useState<
    Array<any>
  >([]);
  const [showArchives, setShowArchives] = useState<boolean>(false);
  const [waitingForResponse, setWaitingForResponse] = useState<boolean>(false);
  const [chatInput, setChatInput] = useState<string>("");
  const [chatOverlay, setChatOverlay] = useState<number>(1);
  const [webllmProcessor, setWebllmProcessor] = useState<MobileQwen3WebLLMProcessor | null>(null);
  const [qwen3Manager, setqwen3Manager] = useState<Qwen3Manager | null>(null);
  const [activeRuntime, setActiveRuntime] = useState<'mobile_qwen3' | 'tiny_qwen3' | 'qwen3' | 'unsupported'>('mobile_qwen3');
  const [localCapability, setLocalCapability] = useState<LocalModelCapability | null>(null);
  const localCapabilityRef = useRef<LocalModelCapability | null>(null);

  function rememberLocalCapability(cap: LocalModelCapability) {
    localCapabilityRef.current = cap;
    setLocalCapability(cap);
  }
  const [loadProgress, setLoadProgress] = useState<ModelLoadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('idle');
  const decryptionManagerRef = useRef<DecryptionManager | null>(null);
  const [isEncryptionReady, setIsEncryptionReady] = useState(false);
  const task6AbortRef = useRef<AbortController | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [showTokenLimitModal, setShowTokenLimitModal] = useState<boolean>(false);
  const [permissionDeniedMsg, setPermissionDeniedMsg] = useState<string | null>(null);
  const activeStreamAbortRef = useRef<AbortController | null>(null);
  const activeResponseIdRef = useRef<string | null>(null);

  // Conversation IDs are returned by the backend and mirrored in React state.
  // Do not read/write conversation_id via document.cookie; the backend owns that cookie.
  const initialConversationId =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem(`activeConversationId/${TOOL_ID}`)
      : null;
  const [activeConversationId, setActiveConversationId] = useState<string | null>(initialConversationId);
  const activeConversationIdRef = useRef<string | null>(initialConversationId);

  function rememberConversationId(value: string | number | null | undefined) {
    const normalized = value !== null && value !== undefined && String(value).trim() !== ''
      ? String(value)
      : null;

    activeConversationIdRef.current = normalized;
    setActiveConversationId(normalized);

    if (typeof window !== 'undefined') {
      const key = `activeConversationId/${TOOL_ID}`;
      if (normalized) {
        window.sessionStorage.setItem(key, normalized);
      } else {
        window.sessionStorage.removeItem(key);
      }
    }
  }

  // Refs for dynamically adjusting message list padding
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const archiveSidebarRef = useRef<ArchiveSidebarRef>(null);
  const inputAreaRef = useRef<HTMLDivElement | null>(null);
  const scrollAfterSendRef = useRef<boolean>(false);

  // ── Credentials Modal State ──
  const [credentialModalState, setCredentialModalState] = useState<any | null>(null);
  const [credentialFormValues, setCredentialFormValues] = useState<Record<string, string>>({});
  const [isSubmittingCredentials, setIsSubmittingCredentials] = useState(false);

  const deepAgentContextsRef = useRef<Record<number, {
    responseId: string;
    sseEndpoint: string;
    basePayload: any;
  }>>({});

  const deepAgentMonitorStoppedRef = useRef<Record<number, boolean>>({});
  const deepAgentLastProgressKeyRef = useRef<Record<number, string>>({});

  // ── Voice Recording State ──
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);

  // ── Voice Recording Refs ──
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // ===== File Upload State (Multi-File) =====
  interface UploadedFile {
    id: number; // Corresponds to the uploadID from the server
    name: string;
    size: number;
    type: string;
    status: 'uploading' | 'success' | 'error';
    // NEW: Store a local preview URL for images
    previewUrl?: string;
  }
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);  // ← NEW

  // File upload drag-and-drop
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const dragCounter = useRef<number>(0);

  // ===== Task Notification State =====
  interface TaskNotification {
    id: string;
    title: string;
    message: string;
    conversationId?: number;
    taskId?: number;
    isHiding?: boolean;
  }
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);

  // Dynamic textarea ID based on tool title
  const textareaId = `${title.replace(/\s+/g, "")}AutoResizeTextarea`;

  // For auto-resizing the textarea
  let textArea = document.getElementById(textareaId);
  if (textArea) {
    textArea.addEventListener("input", function () {
      resizeTextArea(this);
    });
  }

  const openArchives = () => {
    setShowArchives(true);
    fetchArchives(); // Ensure data is fetched when opening archives
  };

  const [isInitialRender, setIsInitialRender] = useState(true);

  useEffect(() => {
    // When messages are cleared (new chat), scroll to top
    if (!isInitialRender && chatBotMessages.length === 0) {
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    // After user sends a long message, scroll to bottom ONCE, then reset the flag
    if (scrollAfterSendRef.current) {
      scrollAfterSendRef.current = false;
      requestAnimationFrame(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      });
      return;
    }

    // During streaming / waiting — do NOT auto-scroll. User is free to scroll.
  }, [chatBotMessages, isInitialRender]);

  // Dynamically adjust message list padding to prevent overlap with the input bar
  useEffect(() => {
    const messageListEl = messageListRef.current;
    const inputAreaEl = inputAreaRef.current;

    if (!messageListEl || !inputAreaEl) return;

    // Use ResizeObserver to detect any change in the input area's height
    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        const newHeight = entry.contentRect.height;
        // Apply the height as padding to the bottom of the message list
        messageListEl.style.paddingBottom = `${newHeight + 20}px`;
      }
    });

    observer.observe(inputAreaEl);

    // Cleanup function to disconnect the observer when the component unmounts
    return () => {
      observer.disconnect();
    };
  }, []); // Empty dependency array means this runs once on mount

  const enableScheduledTaskNotifications = () => {
    if (hasEnabledScheduledTaskNotificationsRef.current) return;
  
    const auth0Sub = getCookieValue("auth0_sub");
    if (!auth0Sub) return;
  
    hasEnabledScheduledTaskNotificationsRef.current = true;
  
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch((error) => {
        console.warn("Notification permission request failed:", error);
      });
    }
  
    notificationUnsubscribeRef.current = subscribeToScheduledTaskNotifications(
      auth0Sub,
      (data) => {
        const normalizedStatus = String(data.status || '').toLowerCase();

        // Log the raw debug payload for developers
        console.debug('[ScheduledTaskNotification] raw payload:', {
          task_id: data.task_id,
          task_name: data.task_name,
          status: data.status,
          conversation_id: data.conversation_id,
          message_length: (data.message || '').length,
          debug_payload: (data as any).debug_payload || {}
        });
        
        // ── AUTO-INJECT: If this notification is for the active conversation,
        // append the result as a new assistant message with BotFileDownloadCard. ──
        const currentConvId = getCookieValue("conversation_id");
        const isCurrentConversation =
          data.conversation_id && String(data.conversation_id) === String(currentConvId);

        if (normalizedStatus === 'success' && isCurrentConversation) {
          const debugPayload = (data as any).debug_payload || {};
          const artifacts = debugPayload.artifacts || data.artifacts || [];
          const messageText = data.message || debugPayload.summary || 'Task completed.';

          // Build botAttachments array (same shape as file_attachment SSE handler uses)
          const botAttachments = artifacts
            .filter((a: any) => a.download_url)
            .map((a: any) => ({
              fileId: a.file_id || '',
              fileName: a.file_name || a.filename || 'file',
              fileSize: a.file_size || 0,
              fileType: a.mime_type || a.file_type || 'application/octet-stream',
              description: a.description || '',
              downloadUrl: a.download_url,
            }));

          // Always show a current-conversation completion in chat. If there are
          // artifacts, include attachment cards; if not, still show the plain
          // completion message, e.g. "Email sent successfully via Outlook."
          const newMsg: UniversalMessageProps = {
            isBotResponse: true,
            messageHtml: messageText,
            timeStamp: formatTime(new Date()),
            ...(botAttachments.length > 0 ? { botAttachments } : {}),
          };
          setChatBotMessages((prev) => [...prev, newMsg]);

          // Suppress toast because the completion is now visible inline.
          return;
        }

        const notificationTitle =
          normalizedStatus === 'action_required'
            ? 'Enlive needs your input'
            : 'Enlive update';

        // Auto-open the conversation for action_required so the user
        // immediately sees what to clarify, instead of having to find
        // and click the toast button.
        if (normalizedStatus === 'action_required' && data.conversation_id) {
          loadArchive(data.conversation_id);
        }

        const newNotification: TaskNotification = {
          id: crypto.randomUUID(),
          title: notificationTitle,
          message:
            data.message ||
            `I finished the scheduled task${data.task_name ? ` "${data.task_name}"` : ''}.`,
          conversationId: data.conversation_id,
          taskId: data.task_id,
        };
                
        setNotifications((prev) => [...prev, newNotification]);
  
        window.setTimeout(() => {
          removeNotification(newNotification.id);
        }, 6000);
  
        if (
          "Notification" in window &&
          Notification.permission === "granted" &&
          document.hidden
        ) {
          const nativeNotif = new Notification(notificationTitle, {
            body: newNotification.message,
            icon: "/favicon.ico",
          });
  
          nativeNotif.onclick = () => {
            window.focus();
            if (data.conversation_id) {
              loadArchive(data.conversation_id);
            }
          };
        }
      }
    );
  };

  const removeNotification = (id: string) => {
    // First mark as hiding to trigger CSS animation
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isHiding: true } : n));
    // Then remove from DOM after animation completes
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 300);
  };

  useEffect(() => {
    return () => {
      notificationUnsubscribeRef.current?.();
      notificationUnsubscribeRef.current = null;
      hasEnabledScheduledTaskNotificationsRef.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      Object.keys(deepAgentMonitorStoppedRef.current).forEach((key) => {
        deepAgentMonitorStoppedRef.current[Number(key)] = true;
      });
    };
  }, []);

  // ================================
  // Stop Generation
  // ================================
  const handleStopGeneration = () => {
    if (activeStreamAbortRef.current) {
      activeStreamAbortRef.current.abort();
      activeStreamAbortRef.current = null;
    }
  
    setWaitingForResponse(false);
    setLoadingStage('idle');
  
    const responseId = activeResponseIdRef.current;
    if (!responseId) return;
  
    updateAssistantMessageById(responseId, (msg) => {
      const currentHtml = typeof msg.messageHtml === 'string' ? msg.messageHtml : '';
  
      const cleanedHtml = currentHtml.replace(/\[\u200B\]\(#cursor\)\s*$/, '');
  
      return {
        ...msg,
        isLoading: false,
        loadingText: '',
        messageHtml: `${cleanedHtml}  
    
  <em>[Generation stopped by user]</em>`,
      };
    });
  };
  
  // ================================
  // WebLLM Initialization
  // ================================
  useEffect(() => {
    const initModel = async () => {
      try {
        const cap = await getLocalModelCapability();
        rememberLocalCapability(cap);
        console.log('[Local AI capability]', cap);

        if (!cap.supported) {
          console.warn('Local Enlive AI unsupported:', cap.reason);
          setActiveRuntime('unsupported');
          setWebllmProcessor(null);
          setqwen3Manager(null);
          setError(cap.reason);
          setLoadProgress(null);
          return;
        }

        if (cap.runtime === 'qwen3') {
          const manager = Qwen3Manager.getInstance();
          const unsubscribe = manager.onProgress((progress) => {
            setLoadProgress(progress);
            if (progress.stage === 'ready') setLoadProgress(null);
          });

          try {
            if (!manager.isReady()) {
              await manager.initialize();
            }

            setqwen3Manager(manager);
            setWebllmProcessor(null);
            setActiveRuntime('qwen3');
            console.log('High-local Qwen3.5-4B ready');
          } catch (err) {
            // Local-only downgrade: if 4B fails, try 2B. This is NOT server fallback.
            console.warn('High-local failed; attempting low-local downgrade:', err);
            const { blockHighLocalForOneDay } = await import('./hybrid_model_selection');
            blockHighLocalForOneDay(err instanceof Error ? err.message : String(err));

            const lowManager = MobileQwen3Manager.getInstance();
            const processor = await lowManager.getProcessor();
            setqwen3Manager(null);
            setWebllmProcessor(processor);
            setActiveRuntime('mobile_qwen3');

            const lowCap = await getLocalModelCapability();
            rememberLocalCapability(lowCap);
            console.log('Downgraded to low-local Qwen3.5-2B');
          } finally {
            unsubscribe();
          }
          return;
        }

        if (cap.runtime === 'mobile_qwen3' || cap.runtime === 'tiny_qwen3') {
          const manager = MobileQwen3Manager.getInstance();
          const unsubscribe = manager.onProgress((progress) => {
            setLoadProgress(progress);
            if (progress.stage === 'ready') setLoadProgress(null);
          });

          try {
            const processor = await manager.getProcessor();
            setWebllmProcessor(processor);
            setqwen3Manager(null);
            setActiveRuntime(cap.runtime);
            console.log(cap.runtime === 'tiny_qwen3'
              ? 'Tiny-local Qwen3.5-0.8B ready'
              : 'Low-local Qwen3.5-2B ready');
          } finally {
            unsubscribe();
          }
        }
      } catch (error) {
        console.error('Model initialization failed:', error);
        setActiveRuntime('unsupported');
        setError(error instanceof Error ? error.message : 'Failed to load local model');
        setLoadProgress(null);
      }
    };

    initModel();

    return () => {
      console.log(`ChatbotTool [${title}] unmounting (processor stays in memory)`);
    };
  }, []);

  // ================================
  // On mount: initialize encryption
  // ================================
  useEffect(() => {
    const initializeEncryption = async () => {
      const manager = new DecryptionManager();
      await manager.generateKeypair();
      decryptionManagerRef.current = manager;
      setIsEncryptionReady(true);
    };
    initializeEncryption();
    return () => decryptionManagerRef.current?.clearKeys();
  }, []);

  // ================================
  // On mount: create/load conversation, get overlay
  // *** INCLUDES CONVERSATION STATE BLEED FIX ***
  // ================================
  useEffect(() => {
    const conversationId = activeConversationIdRef.current;

    // If no conversation ID is mirrored in state/sessionStorage, start a backend-owned chat.
    if (!conversationId) {
      handleNewChat();
      fetchUserOverlay();
      return;
    }

    const localKey = `chatBotMessages/${TOOL_ID}_${conversationId}`;
    const stored = localStorage.getItem(localKey);
    if (stored) {
      const parsed = JSON.parse(stored) as UniversalMessageProps[];
      setChatBotMessages(parsed);
    }

    (async () => {
      try {
        const resp = await axios.get(`${API_SERVER_URL}/load-chat-archive`, {
          params: { conversation_id: conversationId },
          withCredentials: true,
        });
        const { conversation, messages } = resp.data;

        if (conversation.tool_id !== TOOL_ID) {
          console.log(
            `⚠️ Conversation ${conversationId} belongs to tool ${conversation.tool_id}, not ${TOOL_ID} (${title}). Starting new chat.`
          );
          handleNewChat();
          return;
        }

        const dbMsgs: UniversalMessageProps[] = [];
        messages.forEach((m: any) => {
          if (m.message_content && m.message_content.trim() !== "") {
            const messageObj: UniversalMessageProps = {
              isBotResponse: m.role === "assistant",
              messageHtml: m.message_content,
              timeStamp: m.createdAt ? formatTime(new Date(m.createdAt)) : "",
            };

            if (m.files && m.files.length > 0) {
              messageObj.attachments = m.files.map((file: any) => ({
                fileName: file.fileName,
                fileSize: file.fileSize || 0,
                fileType: file.fileType || file.fileName.split('.').pop() || 'file',
                uploadID: file.uploadID,
              }));
            }
            
            dbMsgs.push(messageObj);
          }
        });

        setChatBotMessages(dbMsgs);
        localStorage.setItem(localKey, JSON.stringify(dbMsgs));
      } catch (err) {
        console.error("Failed to fetch chat from DB:", err);
        handleNewChat();
      }
    })();

    fetchUserOverlay();
  }, []);

  // Whenever chatBotMessages updates, store in localStorage
  useEffect(() => {
    const conversationId = activeConversationIdRef.current;
    if (!conversationId) return;
    const localKey = `chatBotMessages/${TOOL_ID}_${conversationId}`;
    localStorage.setItem(localKey, JSON.stringify(chatBotMessages));
  }, [chatBotMessages, activeConversationId]);

  // Update loading stage text in the response being worked on
  useEffect(() => {
    if (!waitingForResponse || loadingStage === 'idle' || chatOverlay !== 1) return;
  
    const responseId = activeResponseIdRef.current;
    if (!responseId) return;
  
    updateAssistantMessageById(responseId, (msg) => {
      // If the message is not currently in a loading state, leave it alone.
      // This preserves our new initial state (isLoading: false, messageHtml: cursor)
      if (!msg.isLoading) return msg;
      
      // Do not overwrite if interim text (messageHtml) is currently being displayed.
      // FIX: Add typeof check to satisfy TypeScript since messageHtml can be a BackendResponse object in Stockbot.
      if (
        typeof msg.messageHtml === 'string' && 
        msg.messageHtml.trim() !== '' && 
        msg.messageHtml !== '[\u200B](#cursor)'
      ) {
         return msg;
      }

      return {
        ...msg,
        loadingText: getLoadingMessage(loadingStage),
      };
    });
  }, [loadingStage, waitingForResponse, chatOverlay]);

  // ================================
  // Overlay logic
  // ================================
  async function fetchUserOverlay() {
    try {
      const resp = await axios.get(`${API_SERVER_URL}/get-overlay-settings`, {
        withCredentials: true,
      });
      if (resp.data && resp.data.ChatOverlay !== undefined) {
        const overlayValue = parseInt(resp.data.ChatOverlay, 10);
        setChatOverlay(overlayValue);
        window.sessionStorage.setItem("chatOverlay", String(overlayValue));
      }
    } catch (err) {
      console.error("Failed to fetch overlay:", err);
    }
  }

  async function toggleOverlay() {
    const newValue = chatOverlay === 1 ? 0 : 1;
    try {
      await axios.post(
        `${API_SERVER_URL}/update-overlay`,
        { ChatOverlay: newValue },
        { withCredentials: true }
      );
      setChatOverlay(newValue);
      window.sessionStorage.setItem("chatOverlay", String(newValue));
    } catch (error) {
      console.error("Failed to update overlay:", error);
    }
  }

  // ================================
  // Archives
  // ================================
  async function fetchArchives() {
    try {
      const resp = await axios.get(`${API_SERVER_URL}/list-conversations`, {
        params: { tool_id: TOOL_ID },
        withCredentials: true,
      });
      setArchivedConversations(resp.data);
      setShowArchives(true);
    } catch (err) {
      console.error("Failed to fetch archives:", err);
    }
  }

  async function loadArchive(conversationId: number) {
    try {
      const response = await axios.get(`${API_SERVER_URL}/load-chat-archive`, {
        params: { conversation_id: conversationId },
        withCredentials: true,
      });
      const { conversation, messages } = response.data;

      // Build the messages array with text + optional file attachments
      const loadedMsgs: UniversalMessageProps[] = [];
      messages.forEach((m: any) => {
        if (m.message_content && m.message_content.trim() !== "") {
          const messageObj: UniversalMessageProps = {
            isBotResponse: m.role === "assistant",
            messageHtml: m.message_content,
            timeStamp: m.createdAt ? formatTime(new Date(m.createdAt)) : "",
          };

          // BACKWARD COMPATIBILITY: Handle both new 'files' array and old single file
          if (m.files && m.files.length > 0) {
            messageObj.attachments = m.files.map((file: any) => {
              // Check if this file has a blobName (stored in Azure) and is an image
              const isImage = file.fileType?.match(/image\//i) ||
                file.fileName?.match(/\.(png|jpg|jpeg|gif|webp|heic|heif|bmp|svg)$/i);
              const hasBlobName = !!file.blobName;

              // Construct the secure endpoint URL (backend returns a 302 redirect to Azure SAS URL)
              const secureUrl = hasBlobName
                ? `${API_SERVER_URL}/get-file-url?upload_id=${file.uploadID}`
                : undefined;

              return {
                fileName: file.fileName,
                fileSize: file.fileSize || 0,
                fileType: file.fileType || file.fileName.split('.').pop() || 'file',
                uploadID: file.uploadID,
                previewUrl: (isImage && secureUrl) ? secureUrl : undefined,
                downloadUrl: secureUrl,
              };
            });
          }
          
          loadedMsgs.push(messageObj);
        }
      });

      setChatBotMessages(loadedMsgs);
      setApiChatMessages([]);
      rememberConversationId(conversation.ConversationID);
      setShowArchives(false);
    } catch (err) {
      console.error("Failed to load archive:", err);
    }
  }

  // ================================
  // Starting a new chat
  // ================================
  async function handleNewChat() {
    try {
      const response = await axios.post(
        `${API_SERVER_URL}/newchat`,
        {
          toolID: TOOL_ID,
        },
        { withCredentials: true }
      );
      const newConversationID = response.data.ConversationID;
      rememberConversationId(newConversationID);

      // Clear current chat messages in state
      setChatBotMessages([]);
      setApiChatMessages([]);

      setUploadedFiles([]);
    } catch (error) {
      console.error("Failed to start new chat:", error);
    }
  }

  /**
   * Store chat messages to MySQL database after Mobile Qwen3.5 streaming completes
   */
  async function storeChatMessages(
    userMessage: string,
    assistantMessage: string,
    conversationId: string | null,
    uploadIds: number[]
  ) {
    try {
      const response = await axios.post(
        `${API_SERVER_URL}/store-chat-messages`,
        {
          conversation_id: conversationId ? parseInt(conversationId) : null,
          tool_id: TOOL_ID,
          user_message: userMessage,
          assistant_message: assistantMessage,
          upload_ids: uploadIds,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        console.log("✅ Messages stored successfully");
        
        if (response.data.conversation_id) {
          rememberConversationId(response.data.conversation_id);
        }

        if (!conversationId && response.data.conversation_id && archiveSidebarRef.current) {
          archiveSidebarRef.current.fetchArchives();
        }
      } else {
        console.error("❌ Failed to store messages:", response.data.error);
      }
    } catch (error) {
      console.error("❌ Error calling /store-chat-messages:", error);
    }
  }

  const getLoadingMessage = (stage: LoadingStage): string => {
    switch (stage) {
      case 'thinking':
        return 'Thinking...';
      case 'searching':
        return 'Searching your profile...';
      case 'retrieving':
        return 'Finding relevant details...';
      case 'generating':
        return 'Reviewing my findings...';
      case 'streaming':
        return 'Streaming response...';
      case 'scheduling':
        return 'Scheduling your task...';
      case 'executing':
        return 'Executing the requested action...';
      case 'checking_email':
        return 'Checking your email...';
      case 'cancelling':
        return 'Cancelling the task...';
      case 'refining_context':
        return 'Looking deeper for the right details...';
      case 'idle':
      default:
        return 'Processing...';
    }
  };

  function normalizeLoadingStage(stage: unknown): LoadingStage {
    switch (stage) {
      case 'idle':
      case 'thinking':       // NEW
      case 'searching':
      case 'retrieving':
      case 'generating':
      case 'streaming':
      case 'scheduling':
      case 'executing':
      case 'checking_email':
      case 'cancelling':
      case 'refining_context':   // NEW
        return stage;
      default:
        return 'generating';
    }
  }

  function updateAssistantMessageById(
    responseId: string,
    updater: (msg: UniversalMessageProps) => UniversalMessageProps,
  ) {
    setChatBotMessages((prev) => {
      const idx = prev.findIndex(
        (m) => m.isBotResponse && m.responseId === responseId
      );
  
      if (idx === -1) {
        console.warn('updateAssistantMessageById: assistant placeholder not found', { responseId, prev });
        return prev;
      }
  
      const updated = [...prev];
      updated[idx] = updater(updated[idx]);
      return updated;
    });
  }

  // ================================================================
  // CORE PIPELINE: Executes the full Task 3 → 4 → 5 → LLM flow
  // ================================================================
  async function executePipeline(
    sseEndpoint: string,
    basePayload: any,
    responseId: string,
  ): Promise<SSEResult | null> {
    
    // ── PHASE 0: Triage & Interim Text ──
    const phase0Payload = {
      ...basePayload,
      response_phase: 'phase0',
      persist_user_turn: true,
      persist_assistant_turn: false,
      context_retrieval_triggered: false,
    };
    
    const phase0Result = await connectSSE(sseEndpoint, phase0Payload, responseId);
    
    // NEW: If the LLM called a mid-conversation tool, re-loop immediately!
    if (phase0Result.toolReloopNeeded) {
      console.log('🔄 Tool reloop requested. Re-calling pipeline with tool results...');
      
      const reloopPayload = {
        ...basePayload,
        tool_reloop_triggered: true,
        tool_reloop_result: phase0Result.toolReloopResult,
        interim_text: phase0Result.interimText,
        context_retrieval_triggered: true, // Skip Phase 0 and retrieval on reloop
        enable_query_expansion: false      // Skip Task 3 on reloop
      };
      
      // Recursively call executePipeline with the new payload
      return await executePipeline(sseEndpoint, reloopPayload, responseId);
    }

    // If the LLM didn't need context, it just answered the question. We're done!
    if (!phase0Result.contextRetrievalNeeded) {
      return phase0Result;
    }
    
    // If we get here, the LLM streamed some interim text (e.g., "Let me check...")
    // and then called `get_user_context`. We now trigger the retrieval pipeline.
    console.log('🔄 LLM requested context. Starting retrieval pipeline...');
    
    // ── INTERIM DISPLAY GRACE PERIOD ──
    // Let the user read the interim text for at least 3 seconds.
    // Phase A starts IMMEDIATELY (zero added latency), but its early
    // "status" events are suppressed until the grace period expires.
    // After that, status updates ("Searching...", "Finding details...")
    // resume and replace the interim text as normal.
    //
    // IMPORTANT: We intentionally do NOT call setChatBotMessages here
    // to remove the streaming cursor. React 18 batches state updates
    // in async functions, and a setChatBotMessages call at this point
    // can race with Phase C's complete handler, causing the interim text
    // to overwrite the final LLM response. The temporary blinking cursor span
    // will be naturally replaced when the first unsuppressed status event
    // sets messageHtml to "".
    const hasInterimText = !!phase0Result.interimText?.trim();
    const INTERIM_GRACE_MS = hasInterimText ? 3000 : 0;
    const suppressStatusUntil = Date.now() + INTERIM_GRACE_MS;

    // ── PHASE A: Start immediately — status events suppressed during grace period ──
    const phaseAPayload = {
      ...basePayload,
      response_phase: 'task3',
      persist_user_turn: false,
      persist_assistant_turn: false,
      context_retrieval_triggered: true,
      interim_text: phase0Result.interimText
    };
    const phaseAResult = await connectSSE(sseEndpoint, phaseAPayload, responseId, { suppressStatusUntil });

    if (!(phaseAResult.task3Prompt || phaseAResult.splitPrompt)) {
      return phaseAResult;
    }

    if (!(webllmProcessor || qwen3Manager)) {
      throw new Error(localCapabilityRef.current?.reason || 'Local model is required for Enlive retrieval but is not ready.');
    }

    console.log('🔄 Running Task 3 (Query Expansion) locally...');

    let llmResponseText: string;
    if (phaseAResult.splitPrompt) {
      const decryptedTask3System = await decryptionManagerRef.current!.decrypt(phaseAResult.task3System!);
      const decryptedTask3User = await decryptionManagerRef.current!.decrypt(phaseAResult.task3User!);

      llmResponseText = qwen3Manager
        ? await qwen3Manager.executeTask3(decryptedTask3System, decryptedTask3User)
        : await webllmProcessor!.executeTask3(decryptedTask3System, decryptedTask3User);
    } else {
      const decryptedTask3Prompt = await decryptionManagerRef.current!.decrypt(phaseAResult.task3Prompt!);
      llmResponseText = qwen3Manager
        ? await qwen3Manager.executeTask3(decryptedTask3Prompt, '')
        : await webllmProcessor!.executeTask3(decryptedTask3Prompt);
    }
    console.log(`✅ Task 3 complete`);

    if (qwen3Manager && !llmResponseText.includes('###EXPANDED_QUERIES###')) {
      console.warn('⚠️ Task 3 produced no structured output — sending raw query as fallback');
      const fallbackQuery = basePayload.messages?.slice(-1)?.[0]?.content || '';
      llmResponseText = `###EXPANDED_QUERIES###\n${fallbackQuery}\n###END_EXPANDED_QUERIES###\n###DOCUMENT_REFERENCE###\n###END_DOCUMENT_REFERENCE###\n###METADATA_FILTERS###\n{}\n###END_METADATA_FILTERS###`;
    }

    // ── PHASE B: Send Task 3 result, get Task 4/5 prompts ──
    const phaseBPayload = {
      ...basePayload,
      response_phase: 'task4_5',
      persist_user_turn: false,
      persist_assistant_turn: false,
      enable_query_expansion: false,
      llm_response_text: llmResponseText,
      context_retrieval_triggered: true,     
      interim_text: phase0Result.interimText   
    };
    const phaseBResult = await connectSSE(sseEndpoint, phaseBPayload, responseId);

    const hasDesktopCombinedPrompt =
      !!qwen3Manager &&
      phaseBResult.splitPrompt === true &&
      phaseBResult.combinedTask45 === true &&
      !!phaseBResult.task4System &&
      !!phaseBResult.task4User;

    const hasLegacySplitPrompts =
      phaseBResult.splitPrompt === true &&
      phaseBResult.combinedTask45 !== true &&
      !!phaseBResult.task4System &&
      !!phaseBResult.task4User &&
      !!phaseBResult.task5System &&
      !!phaseBResult.task5UserTemplate;

    const hasMobileCombinedPrompts =
      !!webllmProcessor &&
      phaseBResult.splitPrompt !== true &&
      !!phaseBResult.task4Prompt &&
      !!phaseBResult.task5PromptTemplate;

    if (!(hasDesktopCombinedPrompt || hasLegacySplitPrompts || hasMobileCombinedPrompts)) {
      console.error('Malformed Task 4/5 prompt envelope from backend', {
        runtime: activeRuntime,
        tier: localCapabilityRef.current?.tier,
        splitPrompt: phaseBResult.splitPrompt,
        combinedTask45: phaseBResult.combinedTask45,
        hasTask4Prompt: !!phaseBResult.task4Prompt,
        hasTask5PromptTemplate: !!phaseBResult.task5PromptTemplate,
        hasTask4System: !!phaseBResult.task4System,
        hasTask4User: !!phaseBResult.task4User,
        hasTask5System: !!phaseBResult.task5System,
        hasTask5UserTemplate: !!phaseBResult.task5UserTemplate,
      });
      throw new Error('Malformed local Task 4/5 prompt envelope; refusing to run local WebLLM.');
    }

    if (!(webllmProcessor || qwen3Manager)) {
      throw new Error(localCapabilityRef.current?.reason || 'Local model is required for enhanced prompting but is not ready.');
    }

    console.log('🔄 Running Enhanced Prompting locally...');

    let task4ResultText: string = '';
    let task5ResultText: string = '';

    if (qwen3Manager && hasDesktopCombinedPrompt) {
      // ── DESKTOP COMBINED PATH (single Qwen3.5-4B pass) ──
      const decryptedT4System = await decryptionManagerRef.current!.decrypt(phaseBResult.task4System!);
      const decryptedT4User   = await decryptionManagerRef.current!.decrypt(phaseBResult.task4User!);

      logLocalTaskPayload('Task 4 desktop combined decrypted input', {
        tier: localCapabilityRef.current?.tier,
        runtime: activeRuntime,
        task4System: decryptedT4System,
        task4User: decryptedT4User,
      });
      assertNonEmptyLocalPrompt('Task 4 desktop system', decryptedT4System);
      assertNonEmptyLocalPrompt('Task 4 desktop user', decryptedT4User);

      await new Promise<void>(r => requestAnimationFrame(() => setTimeout(r, 300)));
      const combined = await qwen3Manager.executeTask4(decryptedT4System, decryptedT4User);

      if (combined && combined.length > 0) {
        // Happy path: combined output IS the final task5_result_text payload.
        task4ResultText = combined;
        task5ResultText = combined;
        console.log(`✅ Combined Task 4+5 complete (${combined.length} chars)`);
      } else {
        // ── FALLBACK: combined output failed validation. Re-run as two passes. ──
        // We don't have a Task 5 prompt from the backend (combined_task_4_5=true
        // suppressed it), so we synthesize a minimal wrapper locally. This is
        // safe because the wrapper is deterministic.
        console.warn('⚠️ Combined Task 4+5 failed validation — running local fallback');
        task4ResultText = await qwen3Manager.executeTask4Legacy(decryptedT4System, decryptedT4User);
        const userQueryFallback = (basePayload.messages?.slice(-1)?.[0]?.content || '').trim();
        task5ResultText =
          `<personal_context>\n${task4ResultText}\n</personal_context>\n` +
          `Based on my personal context provided above, ${userQueryFallback}`;
        console.log(`✅ Fallback Task 4+5 complete (${task5ResultText.length} chars)`);
      }

    } else if (hasLegacySplitPrompts) {
      // ── SPLIT LEGACY TWO-PASS PATH (desktop legacy OR mobile low-local split) ──
      const decryptedT4System       = await decryptionManagerRef.current!.decrypt(phaseBResult.task4System!);
      const decryptedT4User         = await decryptionManagerRef.current!.decrypt(phaseBResult.task4User!);
      const decryptedT5System       = await decryptionManagerRef.current!.decrypt(phaseBResult.task5System!);
      const decryptedT5UserTemplate = await decryptionManagerRef.current!.decrypt(phaseBResult.task5UserTemplate!);

      logLocalTaskPayload('Task 4/5 split legacy decrypted input', {
        tier: localCapabilityRef.current?.tier,
        runtime: activeRuntime,
        task4System: decryptedT4System,
        task4User: decryptedT4User,
        task5System: decryptedT5System,
        task5UserTemplate: decryptedT5UserTemplate,
      });
      assertNonEmptyLocalPrompt('Task 4 legacy system', decryptedT4System);
      assertNonEmptyLocalPrompt('Task 4 legacy user', decryptedT4User);
      assertNonEmptyLocalPrompt('Task 5 legacy system', decryptedT5System);
      assertNonEmptyLocalPrompt('Task 5 legacy user template', decryptedT5UserTemplate);

      await new Promise<void>(r => requestAnimationFrame(() => setTimeout(r, 300)));
      task4ResultText = qwen3Manager
        ? await qwen3Manager.executeTask4Legacy(decryptedT4System, decryptedT4User)
        : await webllmProcessor!.executeTask4(decryptedT4System, decryptedT4User);
      console.log(`✅ Task 4 (legacy split) complete (${task4ResultText.length} chars)`);

      await new Promise<void>(r => requestAnimationFrame(() => setTimeout(r, 300)));
      const finalT5User = decryptedT5UserTemplate.replaceAll('{TASK4_RESULT}', task4ResultText);
      assertNonEmptyLocalPrompt('Task 5 legacy final user', finalT5User, {
        task4ResultChars: task4ResultText.length,
      });
      task5ResultText = qwen3Manager
        ? await qwen3Manager.executeTask5(decryptedT5System, finalT5User)
        : await webllmProcessor!.executeTask5(decryptedT5System, finalT5User);
      console.log(`✅ Task 5 (legacy split) complete (${task5ResultText.length} chars)`);

    } else if (webllmProcessor && hasMobileCombinedPrompts) {
      // ── MOBILE / LOW-LOCAL PATH ──
      const decryptedTask4Prompt   = await decryptionManagerRef.current!.decrypt(phaseBResult.task4Prompt!);
      const decryptedTask5Template = await decryptionManagerRef.current!.decrypt(phaseBResult.task5PromptTemplate!);

      logLocalTaskPayload('Task 4 mobile decrypted input', {
        tier: localCapabilityRef.current?.tier,
        runtime: activeRuntime,
        encryptedTask4PromptChars: phaseBResult.task4Prompt?.length || 0,
        task4Prompt: decryptedTask4Prompt,
      });
      assertNonEmptyLocalPrompt('Task 4', decryptedTask4Prompt, {
        encryptedTask4PromptChars: phaseBResult.task4Prompt?.length || 0,
      });

      task4ResultText = await webllmProcessor.executeTask4(decryptedTask4Prompt);
      console.log(`✅ Task 4 complete (${task4ResultText.length} chars)`);

      const finalTask5Prompt = decryptedTask5Template
        .replace(/\{TASK4_RESULT\}|\{task4_result\}|\{profile_insights\}/g, task4ResultText || '')
        .trim();

      logLocalTaskPayload('Task 5 mobile decrypted/final input', {
        tier: localCapabilityRef.current?.tier,
        runtime: activeRuntime,
        task5Template: decryptedTask5Template,
        task4ResultText,
        finalTask5Prompt,
      });
      assertNonEmptyLocalPrompt('Task 5', finalTask5Prompt, {
        task5TemplateChars: decryptedTask5Template?.length || 0,
        task4ResultChars: task4ResultText?.length || 0,
      });

      task5ResultText = await webllmProcessor.executeTask5(finalTask5Prompt);
      console.log(`✅ Task 5 complete (${task5ResultText.length} chars)`);
    } else {
      throw new Error('No compatible local Task 4/5 execution path for received prompt envelope.');
    }

    // ── PHASE C: Send Task 5 result, get final streamed response ──
    if (!task5ResultText) return phaseBResult;

    const phaseCPayload = {
      ...basePayload,
      enable_query_expansion: false,
      enable_enhanced_prompting: true,
      task5_result_text: task5ResultText,
      context_retrieval_triggered: true,       
      interim_text: phase0Result.interimText   
    };

    return await connectSSE(sseEndpoint, phaseCPayload, responseId);
  }

  useEffect(() => {
    (window as any).deepAgentApprovalAction = (
      rawRunId: number | string,
      rawApprovalId: number | string | null,
      decision: 'approve' | 'reject',
    ) => {
      const runId = Number(rawRunId);
      const parsedApprovalId =
        rawApprovalId !== null && rawApprovalId !== undefined && rawApprovalId !== ''
          ? Number(rawApprovalId)
          : undefined;

      void submitDeepAgentApprovalDecision(
        runId,
        decision,
        Number.isFinite(parsedApprovalId as number) ? parsedApprovalId : undefined,
      );
    };

    return () => {
      delete (window as any).deepAgentApprovalAction;
    };
  }, []);

  // ================================
  // Computer Use Confirmation
  // ================================
  useEffect(() => {
    (window as any).confirmComputerUse = async (proceed: boolean, toolCallDataStr?: string) => {

      const basePayload = (window as any)._pendingComputerUsePayload;
      const sseEndpoint = (window as any)._pendingComputerUseEndpoint;
      const responseId = (window as any)._pendingComputerUseResponseId;

      if (!proceed) {
        updateAssistantMessageById(responseId, (msg) => ({
          ...msg,
          isLoading: false,
          loadingText: '',
          messageHtml: 'Task cancelled.',
          timeStamp: formatTime(new Date()),
        }));
        return;
      }

      if (!basePayload || !sseEndpoint || !toolCallDataStr) return;
      
      const toolCallData = JSON.parse(decodeURIComponent(toolCallDataStr));
      
      const confirmedPayload = {
        ...basePayload,
        confirmed_computer_use: true,
        schedule_tool_call_data: toolCallData
      };

      setWaitingForResponse(true);
      setLoadingStage('generating');
      
      // --- PATCH START: Clear the computer use card when proceeding ---
      updateAssistantMessageById(responseId, (msg) => ({
        ...msg,
        isLoading: true,
        loadingText: 'Executing task chain...',
        messageHtml: '',
        sensitiveActionConfirm: undefined, // <-- This removes the sticky card
      }));

      try {
        const pipelineResult = await executePipeline(sseEndpoint, confirmedPayload, responseId);
        setWaitingForResponse(false);
        setLoadingStage('idle');
        
        if (pipelineResult && chatOverlay === 1) {
          runTask6AndMaybeReloop(pipelineResult, sseEndpoint, confirmedPayload, responseId, 0)
            .catch(err => console.error('❌ Background Task 6 error:', err));
        }
      } catch (err: any) {
        console.error("Failed to execute confirmed task:", err);
        setWaitingForResponse(false);
        setLoadingStage('idle');
      }
    };
    
    return () => {
      delete (window as any).confirmComputerUse;
    };
  }, [chatOverlay]);

  // --- NEW: Sensitive Action Confirmation Handler ---
  // ================================
  useEffect(() => {
    (window as any).confirmSensitiveAction = async (proceed: boolean, toolCallDataStr?: string) => {

      const basePayload = (window as any)._pendingSensitiveActionPayload;
      const sseEndpoint = (window as any)._pendingSensitiveActionEndpoint;
      const responseId = (window as any)._pendingSensitiveActionResponseId;

      if (!proceed) {
        updateAssistantMessageById(responseId, (msg) => ({
          ...msg,
          isLoading: false,
          loadingText: '',
          messageHtml: '❌ **Task Cancelled**\n\nYou declined the sensitive action. The task chain has been aborted.',
          sensitiveActionConfirm: undefined,
        }));
        return;
      }

      if (!basePayload || !sseEndpoint || !toolCallDataStr) return;
      
      const toolCallData = JSON.parse(decodeURIComponent(toolCallDataStr));
      
      const confirmedPayload = {
        ...basePayload,
        confirmed_sensitive_action: true,
        schedule_tool_call_data: toolCallData
      };

      setWaitingForResponse(true);
      setLoadingStage('generating');
      
      // --- PATCH START: Clear the confirmation card when proceeding ---
      updateAssistantMessageById(responseId, (msg) => ({
        ...msg,
        isLoading: true,
        loadingText: 'Executing task chain...',
        messageHtml: '',
        sensitiveActionConfirm: undefined, // <-- This removes the sticky card
      }));

      try {
        const pipelineResult = await executePipeline(sseEndpoint, confirmedPayload, responseId);
        setWaitingForResponse(false);
        setLoadingStage('idle');
        
        if (pipelineResult && chatOverlay === 1) {
          runTask6AndMaybeReloop(pipelineResult, sseEndpoint, confirmedPayload, responseId, 0)
            .catch(err => console.error('❌ Background Task 6 error:', err));
        }
      } catch (error) {
        console.error("Pipeline error after sensitive action confirmation:", error);
        setWaitingForResponse(false);
        setLoadingStage('idle');
      }
    };
    
    return () => {
      delete (window as any).confirmSensitiveAction;
    };
  }, [chatOverlay]);

  // ================================
  // Credential Modal Handlers
  // ================================
  const openCredentialModal = (requestData: any) => {
    const normalizedFields = (requestData?.credential_fields || []).map((field: any) => {
      const rawType = field.type || field.field_type || 'text';
      const normalizedType = rawType === 'otp' || rawType === 'token' ? 'text' : rawType;

      return {
        key: field.name,
        label: field.label,
        type: normalizedType,
        required: field.required,
        placeholder: field.placeholder || '',
        helpText: field.help_text || '',
        defaultValue: field.default_value || '',
      };
    });

    const initialValues = Object.fromEntries(
      normalizedFields.map((field: any) => [field.key, field.defaultValue || ''])
    );

    if (requestData?.run_id) {
      rememberDeepAgentContext(
        Number(requestData.run_id),
        requestData.responseId,
        requestData.sseEndpoint,
        requestData.basePayload,
      );
    }

    setCredentialFormValues(initialValues);
    setCredentialModalState({
      ...requestData,
      providerLabel: requestData.service_name,
      description: requestData.message,
      fields: normalizedFields,
      isOpen: true,
    });
    setIsSubmittingCredentials(false);
  };

  const handleCredentialFieldChange = (key: string, value: string) => {
    setCredentialFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetCredentialModal = () => {
    setCredentialModalState(null);
    setCredentialFormValues({});
    setIsSubmittingCredentials(false);
  };

  useEffect(() => {
    const handleGithubOAuthMessage = (event: MessageEvent) => {
      const allowedOrigins = new Set([
        window.location.origin,
        API_SERVER_URL,
        STREAMING_API_SERVER_URL,
      ]);

      if (!allowedOrigins.has(event.origin)) {
        return;
      }

      const data = event.data as any;
      if (!data || data.type !== 'enlive.github.connected') {
        return;
      }

      if (!credentialModalState?.isOpen) {
        return;
      }

      if (
        credentialModalState.oauth_provider &&
        credentialModalState.oauth_provider !== 'github'
      ) {
        return;
      }

      const activeModal = credentialModalState;
      const sseEndpoint = activeModal.sseEndpoint || '/chat-with-retrieval-stream';
      const responseId = activeModal.responseId;
      const basePayload = activeModal.basePayload || {};

      const followupPayload = {
        ...basePayload,
        persist_user_turn: false,
        persist_assistant_turn: false,
        connection_form_submission: {
          run_id: activeModal.run_id,
          service_name: activeModal.service_name || 'GitHub',
          oauth_completed: true,
          saved_connection_id: data.connection_id,
          fields: {},
        },
      };

      setIsSubmittingCredentials(true);
      setCredentialModalState(null);
      setWaitingForResponse(true);
      setLoadingStage('generating');
      updateBotPlaceholder(responseId, 'Finishing the GitHub connection...');

      connectSSE(
        sseEndpoint,
        followupPayload,
        responseId,
        { manageActiveAbortRef: false }
      ).then((pipelineResult) => {
        setIsSubmittingCredentials(false);
        setWaitingForResponse(false);
        setLoadingStage('idle');

        if (pipelineResult?.credentialRequest) {
          openCredentialModal({
            ...pipelineResult.credentialRequest,
            basePayload,
            sseEndpoint,
            responseId,
          });
          return;
        }

        if (pipelineResult?.serviceExecutionStarted?.run_id) {
          void beginDeepAgentRunMonitoring(
            pipelineResult.serviceExecutionStarted.run_id,
            responseId,
            sseEndpoint,
            basePayload,
          );
        }
      }).catch((err) => {
        console.error('GitHub OAuth resume failed:', err);
        setIsSubmittingCredentials(false);
        setWaitingForResponse(false);
        setLoadingStage('idle');
        updateAssistantMessageById(responseId, (msg) => ({
          ...msg,
          isLoading: false,
          loadingText: '',
          deepAgentCard: {
            title: 'GitHub connection could not resume',
            message: 'GitHub authorization completed, but the chat could not resume automatically. Please try the GitHub connection again.',
            tone: 'danger',
          },
          timeStamp: formatTime(new Date()),
        }));
      });
    };

    window.addEventListener('message', handleGithubOAuthMessage);
    return () => window.removeEventListener('message', handleGithubOAuthMessage);
  }, [credentialModalState]);

  const handleCredentialModalCancel = async () => {
    // NEW: Do not allow cancellation if we are currently submitting
    if (isSubmittingCredentials) return;

    const runId = credentialModalState?.run_id;
    const serviceName = credentialModalState?.service_name;
    const responseId = credentialModalState?.responseId;
    const sseEndpoint = credentialModalState?.sseEndpoint || '/chat-with-retrieval-stream';
    const basePayload = credentialModalState?.basePayload || {};
  
    resetCredentialModal();
    setWaitingForResponse(false);
    setLoadingStage('idle');
  
    if (runId && serviceName) {
      try {
        await connectSSE(sseEndpoint, {
          ...basePayload,
          persist_user_turn: false,
          persist_assistant_turn: false,
          connection_form_submission: {
            run_id: runId,
            service_name: serviceName,
            cancelled: true,
          },
        }, responseId, { manageActiveAbortRef: false });
      } catch (err) {
        console.error('Failed to cancel credential request:', err);
      }
    }
  
    if (responseId) {
      updateAssistantMessageById(responseId, (msg) => ({
        ...msg,
        isLoading: false,
        loadingText: '',
        messageHtml: '',
        deepAgentCard: {
          title: 'Connection setup paused',
          message: 'Credential entry was cancelled. You can restart the service connection whenever you are ready.',
          tone: 'warning',
        },
        timeStamp: formatTime(new Date()),
      }));
    }
  };

  const handleCredentialModalConfirm = async () => {
    if (!credentialModalState) return;

    const missingRequired = (credentialModalState.fields || []).find(
      (field: any) => field.required && !(credentialFormValues[field.key] || '').trim()
    );

    if (missingRequired) {
      alert(`Please provide ${missingRequired.label}.`);
      return;
    }

    setIsSubmittingCredentials(true);

    const sseEndpoint = credentialModalState.sseEndpoint || '/chat-with-retrieval-stream';
    const responseId = credentialModalState.responseId;

    const followupPayload = {
      ...(credentialModalState.basePayload || {}),
      persist_user_turn: false,
      persist_assistant_turn: false,
      connection_form_submission: {
        run_id: credentialModalState.run_id,
        service_name: credentialModalState.service_name,
        fields: credentialFormValues,
      },
    };

    try {
      setWaitingForResponse(true);
      setLoadingStage('generating');
      updateBotPlaceholder(responseId, 'Submitting credentials securely...');

      const pipelineResult = await executePipeline(sseEndpoint, followupPayload, responseId);

      // NEW: Only reset the modal after the pipeline has successfully processed the submission
      resetCredentialModal();

      setWaitingForResponse(false);
      setLoadingStage('idle');

      if (pipelineResult?.credentialRequest) {
        openCredentialModal({
          ...pipelineResult.credentialRequest,
          basePayload: credentialModalState.basePayload,
          sseEndpoint,
          responseId,
        });
        return;
      }

      if (pipelineResult?.serviceExecutionStarted?.run_id) {
        void beginDeepAgentRunMonitoring(
          pipelineResult.serviceExecutionStarted.run_id,
          responseId,
          sseEndpoint,
          credentialModalState.basePayload,
        );
        return;
      }

      if (
        pipelineResult?.deepAgentWaitingForApproval ||
        pipelineResult?.deepAgentCompleted ||
        pipelineResult?.deepAgentFailed ||
        pipelineResult?.completeData?.deep_agent_outcome
      ) {
        return;
      }

      if (pipelineResult && chatOverlay === 1) {
        runTask6AndMaybeReloop(pipelineResult, sseEndpoint, followupPayload, responseId, 0)
          .catch((err) => console.error('❌ Background Task 6 error:', err));
      }
    } catch (err) {
      console.error('Failed to submit credentials:', err);
      setWaitingForResponse(false);
      setLoadingStage('idle');
      setIsSubmittingCredentials(false);
    }
  };

  // Expose sendChatBotMessage globally so the ChatBotMessage buttons can trigger it.
  // Wrap it so that clicking a recipient button strips the picker UI from the
  // earlier bubble before the next turn starts — prevents the jarring flicker.
  useEffect(() => {
    (window as any).sendChatBotMessageGlobal = (message: string) => {
      // Remove the recipientDisambiguation prop from any previous assistant bubble
      // so the buttons disappear cleanly the moment the user makes their choice.
      setChatBotMessages((prev) =>
        prev.map((msg) =>
          msg.recipientDisambiguation
            ? { ...msg, recipientDisambiguation: undefined }
            : msg
        )
      );
      sendChatBotMessage(message);
    };
    return () => {
      delete (window as any).sendChatBotMessageGlobal;
    };
  }, [chatInput, waitingForResponse, isEncryptionReady, uploadedFiles, apiChatMessages, chatBotMessages]);

  // ================================
  // Sending a message
  // ================================
  async function sendChatBotMessage(message: string) {
    if (waitingForResponse || message.trim().length === 0) {
      return;
    }
    if (!isEncryptionReady || !decryptionManagerRef.current) {
      console.error("Encryption not ready, cannot send message.");
      setError("Secure connection not ready. Please wait a moment and try again.");
      return;
    }

    const cap = localCapabilityRef.current || await getLocalModelCapability();
    rememberLocalCapability(cap);

    if (chatOverlay === 1 && !cap.supported) {
      const unsupportedText =
        `Local Enlive AI is not supported in this browser/device. ${cap.reason}\n\n` +
        `Upgrade iOS to at least 26.0, use a newer device, or turn off 'Enlive' switch.`;

      setChatBotMessages((prev) => [
        ...prev,
        {
          isBotResponse: false,
          messageHtml: message,
          timeStamp: formatTime(new Date()),
        },
        {
          isBotResponse: true,
          messageHtml: unsupportedText,
          isLoading: false,
          loadingText: '',
          timeStamp: formatTime(new Date()),
        },
      ]);

      setChatInput('');
      setWaitingForResponse(false);
      setLoadingStage('idle');
      return;
    }

    // If the user's message is "long" relative to the viewport, flag for auto-scroll
    const viewportHeight = window.innerHeight;
    const messageLength = message.length;
    // Rough heuristic: ~50 chars per line, ~20px per line
    const estimatedMessageHeight = (messageLength / 50) * 20;
    if (estimatedMessageHeight > viewportHeight * 0.3) {
      scrollAfterSendRef.current = true;
    }

    resizeTextArea(textArea, 60);

    // Collect all successful upload IDs and their metadata
    const successfulUploads = uploadedFiles.filter(f => f.status === 'success');
    const attachmentsData = successfulUploads.map(f => ({
      fileName: f.name,
      fileSize: f.size,
      fileType: f.type,
      uploadID: f.id,
      previewUrl: f.previewUrl,  // ← NEW: pass preview to message history
    }));
    const currentUploadIDs = successfulUploads.map(f => f.id);

    if (successfulUploads.length > 0) {
      // NEW: Revoke preview URLs before clearing
      successfulUploads.forEach(f => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
      setUploadedFiles(prev => prev.filter(f => f.status !== 'success'));
    }

    const newMessage: UniversalMessageProps = {
      isBotResponse: false,
      messageHtml: message,
      timeStamp: formatTime(new Date()),
      attachments: attachmentsData.length > 0 ? attachmentsData : undefined,
    };
    const newAPIMessage: APIChatMessage = {
      content: message,
      role: "user",
    };
    const newAPIMessages = [...apiChatMessages, newAPIMessage];
    setApiChatMessages(newAPIMessages);

    // ── ADD USER MESSAGE + BOT PLACEHOLDER ──
    const initialLoadingText = '';

    const responseId = crypto.randomUUID();
    activeResponseIdRef.current = responseId;
    
    setChatBotMessages((prev) => [
      ...prev,
      newMessage,
      {
        isBotResponse: true,
        responseId,
        messageHtml: "[\u200B](#cursor)", // NEW: Start with just the blinking cursor
        isLoading: false,                 // NEW: Do not trigger the loading placeholder
        loadingText: "",
        timeStamp: formatTime(new Date()),
      },
    ]);
    setChatInput("");
    setWaitingForResponse(true);
    // FIX: Remove this aggressive local state change so the backend controls the status
    // if (chatOverlay === 1) {
    //   setLoadingStage('generating');
    // }

    const conversationId = activeConversationIdRef.current;
    const publicKey = decryptionManagerRef.current.getPublicKeyPEM();

    // ── DETERMINE ENDPOINT ──
    const sseEndpoint = chatOverlay === 1
      ? "/chat-with-retrieval-stream"
      : "/chat-without-retrieval-stream";

    // ── BUILD BASE PAYLOAD ──
    const basePayload = {
      messages: newAPIMessages,
      threshold: 3.5,
      toolID: TOOL_ID,
      conversation_id: conversationId ? parseInt(conversationId) : null,
      uploadIDs: currentUploadIDs,
      enable_query_expansion: true,
      enable_enhanced_prompting: true,
      publicKey: publicKey,
      device_type: cap.backendDeviceType || 'mobile',
      local_model_tier: cap.tier,
      local_model_supported: cap.supported,
      local_model_id: cap.modelId,
      local_model_runtime: cap.runtime,
      local_model_reason: cap.reason,
      local_capability_report: {
        browser: cap.browser,
        isMobile: cap.isMobile,
        isIOS: cap.isIOS,
        adapterInfo: cap.adapterInfo,
        limits: cap.limits,
        features: cap.features,
        deviceMemoryGB: cap.deviceMemoryGB,
        hardwareConcurrency: cap.hardwareConcurrency,
        promptProfile: cap.promptProfile || 'standard',
        riskFlags: cap.riskFlags || {},
        platform: cap.platform,
        devicePixelRatio: cap.devicePixelRatio,
        viewport: cap.viewport,
      },
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      user_local_time: new Date().toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      }),

      // RESPONSE-CYCLE METADATA
      response_cycle_id: responseId,
      response_phase: 'phase0',
      persist_user_turn: true,
      persist_assistant_turn: false,
    };

    // Abort any existing background re-loop when user sends a new message
    if (task6AbortRef.current) {
      task6AbortRef.current.abort();
    }
    task6AbortRef.current = new AbortController();

    // Abort any active SSE stream from the previous turn
    if (activeStreamAbortRef.current) {
      activeStreamAbortRef.current.abort();
      activeStreamAbortRef.current = null;
    }

    try {
      // ── Execute the main pipeline ──
      const pipelineResult = await executePipeline(sseEndpoint, basePayload, responseId);
      
      if (pipelineResult?.computerUseConfirm) {
        setWaitingForResponse(false);
        setLoadingStage('idle');
        
        updateAssistantMessageById(responseId, (msg) => ({
          ...msg,
          isLoading: false,
          loadingText: '',
          messageHtml: '_Action paused — please confirm to continue._',
          computerUseConfirm: pipelineResult.computerUseConfirm,
          timeStamp: formatTime(new Date()),
        }));
        
        (window as any)._pendingComputerUsePayload = basePayload;
        (window as any)._pendingComputerUseEndpoint = sseEndpoint;
        (window as any)._pendingComputerUseResponseId = responseId;
        return;
      }
      
      if (pipelineResult?.sensitiveActionConfirm) {
        setWaitingForResponse(false);
        setLoadingStage('idle');
        
        updateAssistantMessageById(responseId, (msg) => ({
          ...msg,
          isLoading: false,
          loadingText: '',
          messageHtml: '_Action paused — please confirm sensitive actions to continue._',
          sensitiveActionConfirm: pipelineResult.sensitiveActionConfirm,
          timeStamp: formatTime(new Date()),
        }));
        
        (window as any)._pendingSensitiveActionPayload = basePayload;
        (window as any)._pendingSensitiveActionEndpoint = sseEndpoint;
        (window as any)._pendingSensitiveActionResponseId = responseId;
        return;
      }

      if (pipelineResult?.recipientDisambiguation) {
        setWaitingForResponse(false);
        setLoadingStage('idle');
        
        updateAssistantMessageById(responseId, (msg) => ({
          ...msg,
          isLoading: false,
          loadingText: '',
          messageHtml: pipelineResult.recipientDisambiguation!.interim_text,
          recipientDisambiguation: pipelineResult.recipientDisambiguation,
          timeStamp: formatTime(new Date()),
        }));
        return;
      }

      // Handle permission denied
      if (pipelineResult?.permissionDenied) {
        setPermissionDeniedMsg(pipelineResult.permissionDenied);

        updateAssistantMessageById(responseId, (msg) => ({
          ...msg,
          isLoading: false,
          loadingText: '',
          messageHtml: '*[Action blocked: Missing permissions]*',
          timeStamp: formatTime(new Date()),
        }));
        setWaitingForResponse(false);
        setLoadingStage('idle');
        return;
      }

      if (pipelineResult?.credentialRequest) {
        setWaitingForResponse(false);
        setLoadingStage('idle');

        openCredentialModal({
          ...pipelineResult.credentialRequest,
          basePayload,
          sseEndpoint,
          responseId,
        });
        return;
      }

      if (pipelineResult?.serviceExecutionStarted?.run_id) {
        setWaitingForResponse(false);
        setLoadingStage('idle');

        void beginDeepAgentRunMonitoring(
          pipelineResult.serviceExecutionStarted.run_id,
          responseId,
          sseEndpoint,
          basePayload,
        );
        return;
      }

      if (
        pipelineResult?.deepAgentWaitingForApproval ||
        pipelineResult?.deepAgentCompleted ||
        pipelineResult?.deepAgentFailed ||
        pipelineResult?.completeData?.deep_agent_outcome
      ) {
        setWaitingForResponse(false);
        setLoadingStage('idle');
        return;
      }

      // ── Pipeline complete — release the UI lock ──
      setWaitingForResponse(false);
      setLoadingStage('idle');

      // ── Fire-and-forget: Run Task 6 in the background ──
      console.log('🔍 DEBUG Task 6 gate check:', {
        hasPipelineResult: !!pipelineResult,
        chatOverlay,
        hasTask6System: !!pipelineResult?.task6System,
        hasTask6Prompt: !!pipelineResult?.task6Prompt,
        task6SplitPrompt: pipelineResult?.task6SplitPrompt,
        hasQwen3Manager: !!qwen3Manager,
        hasWebllmProcessor: !!webllmProcessor,
        completeDataKeys: pipelineResult?.completeData ? Object.keys(pipelineResult.completeData) : [],
      });
      if (pipelineResult && chatOverlay === 1) {
        runTask6AndMaybeReloop(pipelineResult, sseEndpoint, basePayload, responseId, 0)
          .catch(err => console.error('❌ Background Task 6 error:', err));
      }

    } catch (err: any) {
      console.error("Failed to send message:", err);

      if ((activeRuntime === 'mobile_qwen3' || activeRuntime === 'tiny_qwen3') && isLowLocalDegenerateError(err)) {
        const reason = String(err?.message || 'degenerate low-local output');
        if (activeRuntime === 'tiny_qwen3') {
          localStorage.setItem('qwen3TinyLocalBlockedUntil', String(Date.now() + 24 * 60 * 60 * 1000));
          localStorage.setItem('qwen3TinyLocalBlockedReason', reason);
        } else {
          blockLowLocalAfterDegenerateFailure(reason);
        }

        const displayMessage =
          "Local Enlive AI returned unstable output on this device, so I temporarily disabled local mode for this browser. " +
          "This usually means the device's WebGPU runtime is not reliable enough for the current Qwen3.5-2B local model. " +
          "Please turn off the Enlive switch for this chat, or try again on a newer device.";

        updateAssistantMessageById(responseId, (msg) => ({
          ...msg,
          isLoading: false,
          loadingText: '',
          messageHtml: displayMessage,
          errorMessage: undefined,
          timeStamp: formatTime(new Date()),
        }));

        setActiveRuntime('unsupported');
        setWaitingForResponse(false);
        setLoadingStage('idle');
        return;
      }

      const errorMessage = err?.message || "Sorry, something went wrong. Please try again.";
      const isRateLimitError = errorMessage.includes("daily limit")
        || errorMessage.includes("concurrent connections")
        || errorMessage.includes("Rate limit")
        || errorMessage.includes("Blocked");
      
      const isTokenLimitError = errorMessage.includes("token limit") || errorMessage.includes("token quota");

      const displayMessage = isRateLimitError ? errorMessage : "Sorry, something went wrong. Please try again.";

      setChatBotMessages((prev) => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (updated[lastIdx]?.isBotResponse) {
          updated[lastIdx] = {
            ...updated[lastIdx],
            messageHtml: '',
            errorMessage: displayMessage,
            timeStamp: formatTime(new Date()),
          };
        }
        return updated;
      });
      setWaitingForResponse(false);
      setLoadingStage('idle');
      
      if (isTokenLimitError || errorMessage.includes("daily limit")) {
        setShowTokenLimitModal(true);
      }
    }
  }

  // ================================================================
  // HELPER: Update the bot placeholder loading message
  // ================================================================
  function updateBotPlaceholder(responseId: string, text: string) {
    updateAssistantMessageById(responseId, (msg) => ({
      ...msg,
      isLoading: true,
      loadingText: text,
      messageHtml: '',
    }));
  }  

  function escapeHtml(value: string | undefined | null): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function renderDeepAgentCardHtml(
    title: string,
    message: string,
    tone: 'info' | 'success' | 'warning' | 'danger' = 'info',
    actionsHtml = '',
  ) {
    const eyebrowLabel =
      tone === 'success'
        ? 'Completed'
        : tone === 'warning'
          ? 'Needs attention'
          : tone === 'danger'
            ? 'Action failed'
            : 'Deep Agent';
  
    return `
      <div class="deep-agent-card deep-agent-card--${tone}">
        <div class="deep-agent-card__header">
          <div class="deep-agent-card__title-wrap">
            <div class="deep-agent-card__eyebrow">${eyebrowLabel}</div>
            <div class="deep-agent-card__title">${title}</div>
          </div>
        </div>
        <div class="deep-agent-card__message">${message}</div>
        ${actionsHtml || ''}
      </div>
    `;
  }

  function workflowMessage(data: any, fallback: string): string {
    const text = String(data?.message || data?.summary || fallback || '').trim();
    return text || fallback;
  }

  function deepAgentArtifactsToBotAttachments(artifacts: any[] | undefined) {
    return (artifacts || [])
      .filter((a: any) => a?.download_url || a?.downloadUrl || a?.url)
      .map((a: any) => ({
        fileId: a.file_id || a.fileId || '',
        fileName: a.file_name || a.filename || a.fileName || 'file',
        fileSize: a.file_size || a.fileSize || 0,
        fileType: a.mime_type || a.file_type || a.fileType || 'application/octet-stream',
        description: a.description || '',
        downloadUrl: a.download_url || a.downloadUrl || a.url,
      }));
  }

  function showAssistantProse(
    responseId: string,
    markdown: string,
    extra: Partial<UniversalMessageProps> = {},
  ) {
    updateAssistantMessageById(responseId, (msg) => ({
      ...msg,
      isLoading: false,
      loadingText: '',
      messageHtml: markdown,
      deepAgentCard: undefined,
      timeStamp: msg.timeStamp || formatTime(new Date()),
      ...extra,
    }));
  }

  function rememberDeepAgentContext(
    runId: number,
    responseId: string,
    sseEndpoint: string,
    basePayload: any,
  ) {
    deepAgentContextsRef.current[runId] = {
      responseId,
      sseEndpoint,
      basePayload,
    };
    deepAgentMonitorStoppedRef.current[runId] = false;
  }

  function clearDeepAgentTracking(runId: number, keepContext = false) {
    deepAgentMonitorStoppedRef.current[runId] = true;
    delete deepAgentLastProgressKeyRef.current[runId];

    if (!keepContext) {
      delete deepAgentContextsRef.current[runId];
    }
  }

  const DEEP_AGENT_BASE_INTERVAL_MS = 5000;
  const DEEP_AGENT_MAX_INTERVAL_MS  = 30_000;
  const DEEP_AGENT_MAX_IDLE_POLLS   = 20;   // ~hard ceiling regardless of backoff
  const DEEP_AGENT_MAX_CONSEC_ERRS  = 6;

  async function beginDeepAgentRunMonitoring(
    runId: number,
    responseId: string,
    sseEndpoint: string,
    basePayload: any,
  ) {
    rememberDeepAgentContext(runId, responseId, sseEndpoint, basePayload);

    let idlePollCount = 0;
    let consecutiveErrors = 0;
    let lastProgressKey: string | undefined;

    // Helper: resolve when the tab is visible (or immediately if it already is).
    const waitUntilVisible = () =>
      new Promise<void>((resolve) => {
        if (!document.hidden) return resolve();
        const onVis = () => {
          if (!document.hidden) {
            document.removeEventListener('visibilitychange', onVis);
            resolve();
          }
        };
        document.addEventListener('visibilitychange', onVis);
      });

    while (!deepAgentMonitorStoppedRef.current[runId]) {
      // Pause the loop while the tab is backgrounded — eliminates a huge
      // chunk of unnecessary polling and removes the perfectly-regular
      // cadence that triggers `identical_interval` scoring.
      await waitUntilVisible();
      if (deepAgentMonitorStoppedRef.current[runId]) return;

      const pollPayload = {
        ...basePayload,
        persist_user_turn: false,
        persist_assistant_turn: false,
        deep_agent_run_id: runId,
      };

      try {
        const pollResult = await connectSSE(
          sseEndpoint,
          pollPayload,
          responseId,
          { manageActiveAbortRef: false },
        );

        if (deepAgentMonitorStoppedRef.current[runId]) return;

        // Terminal events — exactly as before.
        if (
          pollResult?.deepAgentCompleted ||
          pollResult?.deepAgentFailed ||
          pollResult?.deepAgentWaitingForApproval
        ) {
          return;
        }

        // Treat "real" progress (a NEW progress key) as a reset signal,
        // not just any deepAgentProgress payload. This is what stops the
        // "queued → queued → queued …" forever case from looking active.
        const progressKey = deepAgentLastProgressKeyRef.current[runId];
        const sawNewProgress =
          Boolean(pollResult?.deepAgentProgress) &&
          progressKey !== undefined &&
          progressKey !== lastProgressKey;

        if (sawNewProgress) {
          idlePollCount = 0;
          lastProgressKey = progressKey;
        } else {
          idlePollCount += 1;
        }

        consecutiveErrors = 0;
      } catch (err) {
        console.error('Deep Agent polling failed:', err);
        consecutiveErrors += 1;
        idlePollCount += 1;

        if (consecutiveErrors >= DEEP_AGENT_MAX_CONSEC_ERRS) {
          renderDeepAgentLostCard(
            runId,
            responseId,
            'We lost the connection to this workflow. Refresh and ask again to retry.',
          );
          clearDeepAgentTracking(runId);
          return;
        }
      }

      // Hard ceiling — the single line that would have stopped your runaway.
      if (idlePollCount >= DEEP_AGENT_MAX_IDLE_POLLS) {
        renderDeepAgentLostCard(
          runId,
          responseId,
          'This workflow has been quiet for too long. It may have failed in the background — refresh and try again.',
        );
        clearDeepAgentTracking(runId);
        return;
      }

      // Exponential backoff with jitter.
      // 1.5s, 3s, 6s, 12s, 24s, 30s (cap), with up to 500ms of jitter.
      const exp    = Math.min(idlePollCount, 5);
      const target = Math.min(
        DEEP_AGENT_BASE_INTERVAL_MS * 2 ** exp,
        DEEP_AGENT_MAX_INTERVAL_MS,
      );
      const jitter = Math.random() * 500;
      await new Promise((resolve) => setTimeout(resolve, target + jitter));
    }
  }

  // Tiny helper — same UI pattern you already use for failed/cancelled cards.
  function renderDeepAgentLostCard(runId: number, responseId: string, msg: string) {
    updateAssistantMessageById(responseId, (m) => ({
      ...m,
      isLoading: false,
      loadingText: '',
      messageHtml: '',
      deepAgentCard: {
        title: 'Workflow status unavailable',
        message: msg,
        tone: 'danger',
      },
      timeStamp: formatTime(new Date()),
    }));
  }

  async function submitDeepAgentApprovalDecision(
    runId: number,
    decision: 'approve' | 'reject',
    approvalId?: number,
  ) {
    const ctx = deepAgentContextsRef.current[runId];
    if (!ctx) {
      console.error('No Deep Agent context found for approval submission:', { runId, approvalId, decision });
      return;
    }

    updateAssistantMessageById(ctx.responseId, (msg) => ({
      ...msg,
      isLoading: false,
      loadingText: '',
      messageHtml: '',
      deepAgentCard: {
        title: decision === 'approve' ? 'Submitting approval' : 'Rejecting action',
        message: decision === 'approve'
          ? 'Your approval is being submitted. The workflow will resume if the backend accepts it.'
          : 'Your rejection is being submitted to the workflow.',
        tone: 'info',
      },
      timeStamp: formatTime(new Date()),
    }));

    const approvalPayload = {
      ...ctx.basePayload,
      persist_user_turn: false,
      persist_assistant_turn: false,
      deep_agent_run_id: runId,
      deep_agent_approval_decision: {
        run_id: runId,
        approval_id: approvalId,
        decision,
      },
    };

    const result = await connectSSE(
      ctx.sseEndpoint,
      approvalPayload,
      ctx.responseId,
      { manageActiveAbortRef: false },
    );

    if (result?.serviceExecutionStarted?.run_id) {
      void beginDeepAgentRunMonitoring(
        result.serviceExecutionStarted.run_id,
        ctx.responseId,
        ctx.sseEndpoint,
        ctx.basePayload,
      );
      return;
    }

    if (result?.deepAgentProgress) {
      void beginDeepAgentRunMonitoring(runId, ctx.responseId, ctx.sseEndpoint, ctx.basePayload);
    }
  }

  interface CredentialField {
    name: string;
    label: string;
    type?: 'text' | 'password' | 'url' | 'token' | 'otp' | 'textarea';
    field_type?: 'text' | 'password' | 'url' | 'token' | 'otp' | 'textarea';
    required: boolean;
    placeholder?: string;
  }

  interface DeepAgentProgressPayload {
    run_id: number;
    service_name?: string;
    message?: string;
    phase?: string;
    status?: string;
  }

  interface DeepAgentApprovalPayload {
    run_id: number;
    service_name?: string;
    message?: string;
    approval_id?: number;
    requested_action_summary?: string;
  }

  interface DeepAgentTerminalPayload {
    run_id: number;
    service_name?: string;
    message?: string;
    summary?: string;
    error_code?: string;
    retryable?: boolean;
    output_payload?: any;
    artifacts?: any[];
    preview?: string;
  }

  type Task6DecisionType = 'NO_ACTION' | 'AUTO_RESPOND_WITH_CONTEXT' | 'WAIT_FOR_USER';

  interface Task6Decision {
    decision: Task6DecisionType;
    requested_info: string;
    retrieval_query: string;
    confidence: number;
    rationale_short: string;
  }

  interface Task6Context {
    eligible?: boolean;
    is_task6_reloop?: boolean;
    loop_iteration?: number;
    previous_decision?: any;
    stop_reason?: string;
    answer_analysis?: {
      personal_context_fact_count?: number;
      matched_evidence_tokens?: string[];
      answer_contains_personal_context_fact?: boolean;
    };
    tool_outcome?: {
      kind?: string | null;
      status?: string | null;
      is_terminal?: boolean;
    };
    inner_loop?: {
      steps_used?: number;
      force_finalized?: boolean;
      force_finalize_reason?: string | null;
    };
    retrieval?: {
      queries?: string[];
      top_result_count?: number;
      rewrite_count?: number;
      append_count?: number;
      task4_context_count?: number;
      no_relevant_context?: boolean;
    };
  }

  // ================================================================
  // HELPER: Connect to an SSE endpoint and process events
  // ================================================================
  interface SSEResult {
    // Mobile path (single combined prompt)
    task3Prompt?: string;
    task4Prompt?: string;
    task5PromptTemplate?: string;
    // Desktop path (split system/user prompts)
    task3System?: string;
    task3User?: string;
    task4System?: string;
    task4User?: string;
    task5System?: string;
    task5UserTemplate?: string;
    // Flag: true when backend sent split prompts (desktop path)
    splitPrompt?: boolean;
    combinedTask45?: boolean;   // NEW: true when desktop backend combined Task 4 + Task 5
    // Final response data
    completeData?: any;
    // File attachment from LLM tool call
    fileAttachment?: {
      file_id: string;
      filename: string;
      file_size: number;
      file_type: string;
      description: string;
      download_url: string;
    };
    // NEW: Task 6 fields
    task6System?: string;
    task6User?: string;
    task6Prompt?: string;
    task6SplitPrompt?: boolean;
    task6Context?: Task6Context;
    computerUseConfirm?: {
      schedule_tool_call_data: any;
      message: string;
    };
    sensitiveActionConfirm?: {
      schedule_tool_call_data: any;
      message: string;
    };
    permissionDenied?: string;
    contextRetrievalNeeded?: boolean;
    interimText?: string;
    toolReloopNeeded?: boolean;
    toolReloopResult?: string;
    recipientDisambiguation?: {
      search_name: string;
      candidates: Array<{name: string, email: string}>;
      interim_text: string;
    };
    // NEW: Phase C inner-loop telemetry. Optional — only present when
    // the backend ran one or more get_user_context refinement steps.
    innerLoopStepsUsed?: number;
    innerLoopForceFinalized?: boolean;
    innerLoopForceFinalizeReason?: 'duplicate_refined_query' | 'max_steps_reached';
    credentialRequest?: {
      run_id: number;
      service_name: string;
      message: string;
      credential_fields: CredentialField[];
      allow_persistent_storage?: boolean;
      oauth_connect_url?: string | null;
      oauth_provider?: string | null;
    };
    serviceExecutionStarted?: {
      run_id?: number;
      message: string;
      celery_id?: string;
      service_name?: string;
      execution_mode?: string;
    };
    deepAgentProgress?: DeepAgentProgressPayload;
    deepAgentWaitingForApproval?: DeepAgentApprovalPayload;
    deepAgentCompleted?: DeepAgentTerminalPayload;
    deepAgentFailed?: DeepAgentTerminalPayload;
  }

  interface ConnectSSEOptions {
    /** Unix timestamp (ms) before which 'status' events are silently ignored. */
    suppressStatusUntil?: number;
    manageActiveAbortRef?: boolean;
  }

  async function connectSSE(
    endpoint: string,
    payload: any,
    responseId: string,
    options: ConnectSSEOptions = {}
  ): Promise<SSEResult> {
    return new Promise<SSEResult>((resolve, reject) => {
      const result: SSEResult = {};
      let accumulatedHtml = '';
      let hasStartedStreaming = false;
      let resolved = false;

      const ctrl = new AbortController();
      if (options.manageActiveAbortRef !== false) {
        activeStreamAbortRef.current = ctrl;
      }

      fetchEventSource(`${STREAMING_API_SERVER_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
        signal: ctrl.signal,

        onopen: async (response) => {
          if (!response.ok) {
            throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
          }
          console.log(`✅ SSE connected to ${endpoint}`);
        },

        onmessage: async (ev) => {
          let data: any = {};
          try {
            data = ev.data ? JSON.parse(ev.data) : {};
          } catch (parseErr) {
            console.error('Failed to parse SSE payload:', parseErr, ev);
            return;
          }

          switch (ev.event) {
            case 'status': {
              const isPhase0 = !payload.context_retrieval_triggered;

              // During the very first SSE leg, never let status text replace
              // the placeholder or beat the interim LLM text onto the screen.
              if (isPhase0) {
                console.log(`⏳ Ignoring Phase 0 status "${data.message}" so interim text can be first visible content`);
                break;
              }

              // During Phase A / B / C, preserve the interim-text grace window.
              if (options.suppressStatusUntil && Date.now() < options.suppressStatusUntil) {
                console.log(`⏳ Suppressing status "${data.message}" — interim text still visible`);
                break;
              }

              const stage = normalizeLoadingStage(data.stage);
              setLoadingStage(stage);

              const statusMessage = getLoadingMessage(stage);

              if (!hasStartedStreaming) {
                updateAssistantMessageById(responseId, msg => {
                  const existingText =
                    typeof msg.messageHtml === 'string' &&
                    msg.messageHtml.trim() &&
                    msg.messageHtml !== '[\u200B](#cursor)';

                  if (existingText) return msg;

                  return {
                    ...msg,
                    isLoading: true,
                    loadingText: statusMessage,
                    messageHtml: '',
                  };
                });
              }
              break;
            }
            
            case 'context_retrieval_needed': {
              const interimText = (data.interim_text || '').trim();
            
              if (interimText && !hasStartedStreaming) {
                updateAssistantMessageById(responseId, msg => ({
                  ...msg,
                  isLoading: false,
                  loadingText: '',
                  messageHtml: interimText,
                  timeStamp: msg.timeStamp || formatTime(new Date()),
                }));
              }
            
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                result.contextRetrievalNeeded = true;
                result.interimText = interimText;
                resolve(result);
              }
              break;
            }
            
            case 'tool_reloop_needed': {
              const interimText = (data.interim_text || '').trim();
              const suppressInterimRender = data.suppress_interim_render === true;
            
              if (interimText && !hasStartedStreaming && !suppressInterimRender) {
                updateAssistantMessageById(responseId, msg => ({
                  ...msg,
                  isLoading: false,
                  loadingText: '',
                  messageHtml: interimText,
                  timeStamp: msg.timeStamp || formatTime(new Date()),
                }));
              }
            
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                result.toolReloopNeeded = true;
                result.toolReloopResult = data.tool_result_text;
                // Still preserve interimText for the next backend call; only the
                // visible rendering was suppressed.
                result.interimText = interimText;
                resolve(result);
              }
              break;
            }
            
            case 'recipient_disambiguation_needed': {
              const interimText = (data.interim_text || '').trim();
              if (interimText && !hasStartedStreaming) {
                updateAssistantMessageById(responseId, msg => ({
                  ...msg,
                  isLoading: false,
                  loadingText: '',
                  messageHtml: interimText,
                  timeStamp: msg.timeStamp || formatTime(new Date()),
                }));
              }
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                result.recipientDisambiguation = data;
                resolve(result);
              }
              break;
            }


            case 'html_chunk': {
              // ── STREAM HTML PROGRESSIVELY ──
              accumulatedHtml += data.html;

              // Check if we have enough visible content to start showing
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = accumulatedHtml;
              const hasVisibleContent = (tempDiv.textContent?.trim().length ?? 0) > 5;

              if (!hasStartedStreaming && hasVisibleContent) {
                hasStartedStreaming = true;
              }

              if (hasStartedStreaming) {
                updateAssistantMessageById(responseId, (msg) => ({
                  ...msg,
                  isLoading: false,
                  loadingText: '',
                  messageHtml: `${accumulatedHtml}[\u200B](#cursor)`,
                }));
              }
              break;
            }
            
            case 'inner_tool_call_started': {
              // ── BACKEND IS DOING A get_user_context REFINEMENT INSIDE PHASE C ──
              const stepIndex = Number(data.step_index) || 0;
              const maxSteps  = Number(data.max_steps) || 0;
              const refined   = (data.refined_query || '').toString().trim();

              setLoadingStage('refining_context');

              // If we have NOT yet rendered any visible streamed text, replace
              // the loading line with a richer message. If text IS already on
              // screen, we deliberately do nothing to the body — the cursor and
              // the partially-rendered answer stay put — and only the global
              // loadingStage badge changes.
              if (!hasStartedStreaming) {
                const niceLine = 'I’m checking a little more context before I answer...';
                updateAssistantMessageById(responseId, msg => ({
                  ...msg,
                  isLoading: true,
                  loadingText: niceLine,
                }));
              }
              break;
            }

            case 'inner_tool_call_completed': {
              // Refinement step finished. We don't change visible body text;
              // the model is about to either stream more html_chunk or call
              // the tool again. Just record telemetry on the result object.
              result.innerLoopStepsUsed = Math.max(
                result.innerLoopStepsUsed || 0,
                Number(data.step_index) || 0,
              );
              break;
            }

            case 'inner_loop_force_finalized': {
              // Backend hit the cap (or detected a duplicate refinement) and
              // is forcing the model to answer with what it has. Surface this
              // to the result object so callers can display a soft warning if
              // they want to.
              result.innerLoopForceFinalized = true;
              const reason = (data.reason || '').toString();
              if (reason === 'duplicate_refined_query' || reason === 'max_steps_reached') {
                result.innerLoopForceFinalizeReason = reason;
              }
              break;
            }

            case 'file_attachment': {
              // ── HANDLE LLM-GENERATED FILE ──
              result.fileAttachment = data;
              
              // Build the full download URL
              const downloadUrl = data.download_url;
              
              updateAssistantMessageById(responseId, (msg) => {
                const existingAttachments = msg.botAttachments || [];
              
                return {
                  ...msg,
                  botAttachments: [
                    ...existingAttachments,
                    {
                      fileId: data.file_id,
                      fileName: data.filename,
                      fileSize: data.file_size,
                      fileType: data.file_type,
                      description: data.description,
                      downloadUrl,
                    },
                  ],
                };
              });
              break;
            }
            
            case 'task3_prompt': {
              if (data.split_prompt) {
                // Desktop path: separate system/user
                result.task3System = data.task3_system;
                result.task3User = data.task3_user;
                result.splitPrompt = true;
              } else {
                // Mobile path: single combined prompt
                result.task3Prompt = data.task3_prompt;
              }
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                resolve(result);
              }
              break;
            }
            
            case 'task4_prompt': {
              if (data.split_prompt) {
                // Desktop path
                result.task4System = data.task4_system;
                result.task4User   = data.task4_user;
                result.splitPrompt = true;

                // When the backend combined Task 4 + Task 5, task5_* fields are absent.
                result.combinedTask45 = !!data.combined_task_4_5;

                if (!result.combinedTask45) {
                  // Legacy desktop path: still receive Task 5 prompts.
                  result.task5System       = data.task5_system;
                  result.task5UserTemplate = data.task5_user_template;
                }
              } else {
                // Mobile path: unchanged
                result.task4Prompt         = data.task4_prompt;
                result.task5PromptTemplate = data.task5_prompt_template;
                result.combinedTask45      = false;
              }
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                resolve(result);
              }
              break;
            }
            
            case 'task_subscription_needed': {
              enableScheduledTaskNotifications();
              break;
            }
            
            case 'credential_request': {
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                result.credentialRequest = data;
                resolve(result);
              }
              break;
            }
            
            case 'service_connection_update': {
              const messageText = String(
                data.message || 'I saved the connection details.'
              ).trim();

              updateAssistantMessageById(responseId, (msg) => ({
                ...msg,
                isLoading: false,
                loadingText: '',
                messageHtml: messageText,
                deepAgentCard: undefined,
                timeStamp: msg.timeStamp || formatTime(new Date()),
              }));
              break;
            }
            
            case 'service_execution_started': {
              if (data.run_id) {
                rememberDeepAgentContext(data.run_id, responseId, endpoint, payload);
              }

              const messageText = String(
                data.message ||
                'I’ve started that workflow in the background and will update this chat when it finishes.'
              ).trim();

              updateAssistantMessageById(responseId, (msg) => ({
                ...msg,
                isLoading: false,
                loadingText: '',
                messageHtml: messageText,
                deepAgentCard: undefined,
                timeStamp: msg.timeStamp || formatTime(new Date()),
              }));

              result.serviceExecutionStarted = data;
              break;
            }
            
            case 'deep_agent_progress': {
              const runId = Number(data.run_id);
              if (runId) {
                rememberDeepAgentContext(runId, responseId, endpoint, payload);
              }

              const progressKey = JSON.stringify([
                data.run_id,
                data.phase || '',
                data.status || '',
                data.message || '',
              ]);

              if (runId && deepAgentLastProgressKeyRef.current[runId] === progressKey) {
                break;
              }

              if (runId) {
                deepAgentLastProgressKeyRef.current[runId] = progressKey;
              }

              const messageText = String(
                data.message || 'I’m still working through that workflow.'
              ).trim();

              updateAssistantMessageById(responseId, (msg) => ({
                ...msg,
                isLoading: false,
                loadingText: '',
                messageHtml: messageText,
                deepAgentCard: undefined,
                timeStamp: msg.timeStamp || formatTime(new Date()),
              }));

              result.deepAgentProgress = data;
              break;
            }

            case 'deep_agent_waiting_for_approval': {
              const runId = Number(data.run_id);
              const approvalId = data.approval_id;

              if (runId) {
                rememberDeepAgentContext(runId, responseId, endpoint, payload);
                clearDeepAgentTracking(runId, true);
              }

              updateAssistantMessageById(responseId, (msg) => ({
                ...msg,
                isLoading: false,
                loadingText: '',
                messageHtml: '',
                deepAgentCard: {
                  title: data.service_name ? `${data.service_name} requires approval` : 'Approval required',
                  message: data.message || 'The workflow needs your approval before it can continue.',
                  tone: 'warning',
                  runId: runId,
                  approvalId: approvalId,
                },
                timeStamp: formatTime(new Date()),
              }));

              ctrl.abort();
              if (!resolved) {
                resolved = true;
                result.deepAgentWaitingForApproval = data;
                resolve(result);
              }
              break;
            }

            case 'deep_agent_completed': {
              const runId = Number(data.run_id);
              if (runId) {
                clearDeepAgentTracking(runId);
              }

              const botAttachments = (data.artifacts || [])
                .filter((a: any) => a && (a.download_url || a.downloadUrl || a.url || a.storage_uri))
                .map((a: any) => ({
                  fileId: a.file_id || a.fileId || '',
                  fileName: a.file_name || a.filename || a.fileName || 'file',
                  fileSize: a.file_size || a.fileSize || 0,
                  fileType: a.mime_type || a.file_type || a.fileType || 'application/octet-stream',
                  description: a.description || '',
                  downloadUrl: a.download_url || a.downloadUrl || a.url || a.storage_uri,
                }));

              const messageText = String(
                data.message || data.summary || 'I finished that workflow.'
              ).trim();
            
              updateAssistantMessageById(responseId, (msg) => ({
                ...msg,
                isLoading: false,
                loadingText: '',
                messageHtml: messageText,
                deepAgentCard: undefined,
                botAttachments: botAttachments.length > 0 ? botAttachments : msg.botAttachments,
                timeStamp: formatTime(new Date()),
              }));
            
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                result.deepAgentCompleted = data;
                resolve(result);
              }
              break;
            }

            case 'deep_agent_failed': {
              const runId = Number(data.run_id);
              if (runId) {
                clearDeepAgentTracking(runId);
              }

              const botAttachments = (data.artifacts || [])
                .filter((a: any) => a && (a.download_url || a.downloadUrl || a.url || a.storage_uri))
                .map((a: any) => ({
                  fileId: a.file_id || a.fileId || '',
                  fileName: a.file_name || a.filename || a.fileName || 'file',
                  fileSize: a.file_size || a.fileSize || 0,
                  fileType: a.mime_type || a.file_type || a.fileType || 'application/octet-stream',
                  description: a.description || '',
                  downloadUrl: a.download_url || a.downloadUrl || a.url || a.storage_uri,
                }));

              const messageText = String(
                data.message || 'I could not finish that workflow.'
              ).trim();
            
              updateAssistantMessageById(responseId, (msg) => ({
                ...msg,
                isLoading: false,
                loadingText: '',
                messageHtml: messageText,
                deepAgentCard: undefined,
                botAttachments: botAttachments.length > 0 ? botAttachments : msg.botAttachments,
                timeStamp: formatTime(new Date()),
              }));
            
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                result.deepAgentFailed = data;
                resolve(result);
              }
              break;
            }

            case 'computer_use_confirm': {
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                result.computerUseConfirm = data;
                resolve(result);
              }
              break;
            }
            
            case 'sensitive_action_confirm': {
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                result.sensitiveActionConfirm = data;
                resolve(result);
              }
              break;
            }

            case 'permission_denied': {
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                result.permissionDenied = data.message;
                resolve(result);
              }
              break;
            }

            case 'complete': {
              // ── FINAL PAYLOAD RECEIVED ──
              result.completeData = data;
              
              const isDeepAgentControlRequest = Boolean(
                payload.connection_form_submission ||
                payload.deep_agent_run_id ||
                payload.deep_agent_approval_decision
              );

              // NEW: Extract structured Task 6 context and prompt data from the complete payload
              if (data.task6_context) {
                result.task6Context = data.task6_context as Task6Context;
              }

              if (typeof data.inner_loop_steps_used !== 'undefined') {
                result.innerLoopStepsUsed = Number(data.inner_loop_steps_used) || 0;
              }
              if (typeof data.inner_loop_force_finalized !== 'undefined') {
                result.innerLoopForceFinalized = Boolean(data.inner_loop_force_finalized);
              }
              if (data.inner_loop_force_finalize_reason === 'duplicate_refined_query' || data.inner_loop_force_finalize_reason === 'max_steps_reached') {
                result.innerLoopForceFinalizeReason = data.inner_loop_force_finalize_reason;
              }

              if (data.task6_system) {
                result.task6System = data.task6_system;
                result.task6User = data.task6_user;
                result.task6SplitPrompt = true;
              } else if (data.task6_prompt) {
                result.task6Prompt = data.task6_prompt;
                result.task6SplitPrompt = false;
              }
              
              // ── SPECIAL TOOL: TradingView / Stockbot structured widget payload ──
              // Tool 5 needs the complete backend object, not just accumulatedHtml,
              // because StockbotMessage renders TradingView widgets from data.widget.
              if (
                TOOL_ID === 5 &&
                (
                  Object.prototype.hasOwnProperty.call(data, 'widget') ||
                  Object.prototype.hasOwnProperty.call(data, 'response_text') ||
                  Object.prototype.hasOwnProperty.call(data, 'success') ||
                  Object.prototype.hasOwnProperty.call(data, 'error')
                )
              ) {
                  const stockbotPayload = {
                    success: data.success ?? Boolean(data.widget),
                    response_text:
                      data.response_text ||
                      accumulatedHtml ||
                      (data.error ? `I could not load the TradingView widget: ${data.error}` : ''),
                    widget: data.widget ?? null,
                    error: data.error || null,
                  };

                updateAssistantMessageById(responseId, (msg) => ({
                  ...msg,
                  isBotResponse: true,
                  isLoading: false,
                  loadingText: '',
                  messageHtml: stockbotPayload as any,
                  timeStamp: formatTime(new Date()),
                }));

                const finalText = stockbotPayload.response_text || '';
                setApiChatMessages((prev) => [...prev, { content: finalText, role: 'assistant' }]);

                const conversationId = activeConversationIdRef.current;
                await storeChatMessages(
                  payload.messages?.slice(-1)?.[0]?.content || '',
                  JSON.stringify(stockbotPayload),
                  conversationId,
                  payload.uploadIDs || []
                );

                ctrl.abort();
                if (!resolved) {
                  resolved = true;
                  resolve(result);
                }
                break;
              }

              // ── Finalize the streamed HTML ──
              // (Legacy Tasks 1/2 / processBackendResponse path removed —
              //  Qwen3.5 backend now sends fully-formed HTML chunks.)
              const finalAssistantHtml = accumulatedHtml
                || data.final_merged_response_html
                || data.final_enhanced_html
                || (
                  isDeepAgentControlRequest && data.message
                    ? renderDeepAgentCardHtml(
                        data.service_name ? `${data.service_name} update` : 'Workflow update',
                        data.message,
                        data.deep_agent_outcome === 'deep_agent_failed' ? 'danger' : 'info',
                      )
                    : ''
                )
                || "Sorry, I couldn't generate a response.";

              // Remove the blinking cursor
              updateAssistantMessageById(responseId, (msg) => ({
                ...msg,
                isBotResponse: true,
                isLoading: false,
                loadingText: '',
                messageHtml: finalAssistantHtml,
                timeStamp: formatTime(new Date()),
              }));

              // Update API chat history
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = finalAssistantHtml;
              const finalText = tempDiv.textContent || tempDiv.innerText || "";
              setApiChatMessages((prev) => [...prev, { content: finalText, role: "assistant" }]);

              // Store to database
              const conversationId = activeConversationIdRef.current;
              await storeChatMessages(
                payload.messages?.slice(-1)?.[0]?.content || '',
                finalAssistantHtml,
                conversationId,
                payload.uploadIDs || []
              );

              ctrl.abort();
              if (!resolved) {
                resolved = true;
                resolve(result);
              }
              break;
            }
            
            case 'local_model_unsupported': {
              const message = data.message || 'Local Enlive AI is not supported in this browser/device.';
              const reason = data.reason ? `\n\n${data.reason}` : '';

              updateAssistantMessageById(responseId, (msg) => ({
                ...msg,
                isLoading: false,
                loadingText: '',
                messageHtml: `${message}${reason}`,
                timeStamp: formatTime(new Date()),
              }));

              ctrl.abort();
              if (!resolved) {
                resolved = true;
                resolve(result);
              }
              break;
            }

            case 'error': {
              console.error('SSE error event:', data.message);
              ctrl.abort();
              if (!resolved) {
                resolved = true;
                reject(new Error(data.message));
              }
              break;
            }
          }
        },

        onerror: (err) => {
          console.error('SSE connection error:', err);
          
          ctrl.abort();
          if (!resolved) {
            resolved = true;
            reject(err);
          }
        },

        onclose: () => {
          console.log('SSE connection closed');
          if (options.manageActiveAbortRef !== false && activeStreamAbortRef.current === ctrl) {
            activeStreamAbortRef.current = null;
          }
          if (!resolved) {
            resolved = true;
            resolve(result);
          }
        },
      });
    });
  }

  // ================================================================
  // TASK 6: Background follow-up detection and re-loop
  // ================================================================
  function normalizeTask6Decision(value: any): Task6Decision {
    const allowed: Task6DecisionType[] = ['NO_ACTION', 'AUTO_RESPOND_WITH_CONTEXT', 'WAIT_FOR_USER'];
    const rawDecision = String(value?.decision || '').trim().toUpperCase();
    const decision = allowed.includes(rawDecision as Task6DecisionType)
      ? (rawDecision as Task6DecisionType)
      : 'NO_ACTION';

    const confidenceNum = Number(value?.confidence);
    const confidence = Number.isFinite(confidenceNum)
      ? Math.max(0, Math.min(1, confidenceNum))
      : 0;

    return {
      decision,
      requested_info: String(value?.requested_info || '').trim(),
      retrieval_query: String(value?.retrieval_query || '').trim(),
      confidence,
      rationale_short: String(value?.rationale_short || '').trim(),
    };
  }

  function parseTask6Decision(raw: string): Task6Decision {
    const cleaned = String(raw || '')
      .replace(/<think>[\s\S]*?<\/think>/gi, '')
      .trim();

    // Preferred path: strict-ish JSON object emitted by upgraded Task 6 prompt.
    const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        return normalizeTask6Decision(JSON.parse(jsonMatch[0]));
      } catch (err) {
        console.warn('⚠️ Task 6 JSON parse failed; falling back to label parse:', err, cleaned);
      }
    }

    // Backward-compatible fallback for old prompts or mobile path.
    const upper = cleaned.toUpperCase().replace(/[`.!]/g, ' ');
    if (upper.includes('AUTO_RESPOND_WITH_CONTEXT') || upper.includes('FOLLOW_UP')) {
      return {
        decision: 'AUTO_RESPOND_WITH_CONTEXT',
        requested_info: '',
        retrieval_query: '',
        confidence: 0.55,
        rationale_short: 'Fallback label parse from legacy Task 6 output.',
      };
    }
    if (upper.includes('WAIT_FOR_USER')) {
      return {
        decision: 'WAIT_FOR_USER',
        requested_info: '',
        retrieval_query: '',
        confidence: 0.55,
        rationale_short: 'Fallback label parse from Task 6 output.',
      };
    }

    // Fail closed. A malformed classifier output should never trigger an automatic turn.
    return {
      decision: 'NO_ACTION',
      requested_info: '',
      retrieval_query: '',
      confidence: 0,
      rationale_short: 'Unparseable Task 6 output; stopping safely.',
    };
  }

  async function runTask6AndMaybeReloop(
    phaseCResult: SSEResult,
    sseEndpoint: string,
    basePayload: any,
    responseId: string,
    loopIteration: number,
  ) {
    console.log('🔍 DEBUG runTask6AndMaybeReloop entered:', {
      loopIteration,
      hasTask6System: !!phaseCResult.task6System,
      hasTask6Prompt: !!phaseCResult.task6Prompt,
      task6SplitPrompt: phaseCResult.task6SplitPrompt,
      aborted: task6AbortRef.current?.signal.aborted,
      hasQwen3: !!qwen3Manager,
      hasWebllm: !!webllmProcessor,
    });

    // Check abort controller
    if (task6AbortRef.current?.signal.aborted) {
      console.log('🛑 Task 6 re-loop aborted (user sent new message)');
      return;
    }

    // Structured backend stop/terminal guards. These run before invoking Qwen3.5.
    const task6Context = phaseCResult.task6Context || phaseCResult.completeData?.task6_context;

    if (task6Context?.answer_analysis?.answer_contains_personal_context_fact) {
      console.log('⛔ Task 6 skipped: final response already contains retrieved personal-context evidence.', {
        matchedEvidence: task6Context.answer_analysis.matched_evidence_tokens,
        stopReason: task6Context.stop_reason,
      });
      return;
    }

    if (task6Context?.retrieval?.no_relevant_context || task6Context?.stop_reason === 'no_relevant_context') {
      console.log('⛔ Task 6 skipped: backend marked no relevant context found.');
      return;
    }

    if (task6Context?.tool_outcome?.is_terminal) {
      console.log('⛔ Task 6 skipped: backend marked terminal tool outcome.', task6Context.tool_outcome);
      return;
    }

    if (task6Context?.eligible === false) {
      console.log('⛔ Task 6 skipped: backend marked context ineligible.', task6Context.stop_reason || task6Context);
      return;
    }

    const hasTask6 = phaseCResult.task6System || phaseCResult.task6Prompt;
    if (!hasTask6 || !(qwen3Manager || webllmProcessor)) {
      return;
    }

    if (loopIteration >= MAX_TASK6_LOOPS) {
      console.log(`⛔ Max Task 6 loops (${MAX_TASK6_LOOPS}) reached. Stopping.`);
      return;
    }

    // If Phase C already hit a forced-finalize state, avoid a redundant Task 6
    // reloop unless the upgraded classifier later makes a very explicit request.
    // For now, fail closed to preserve the current safe behavior.
    if (phaseCResult.innerLoopForceFinalized || task6Context?.inner_loop?.force_finalized) {
      console.log('⛔ Inner loop was force-finalized; skipping Task 6 reloop.');
      return;
    }

    try {
      // GPU recovery pause before Task 6
      await new Promise<void>(resolve => requestAnimationFrame(() => setTimeout(resolve, 300)));

      if (task6AbortRef.current?.signal.aborted) return;

      let task6Result: string;
      
      console.log('🔍 DEBUG Task 6 about to decrypt and execute:', {
        isDesktopPath: !!(qwen3Manager && phaseCResult.task6SplitPrompt),
        task6SystemLength: phaseCResult.task6System?.length,
        task6UserLength: phaseCResult.task6User?.length,
        task6PromptLength: phaseCResult.task6Prompt?.length,
      });

      if (phaseCResult.task6SplitPrompt) {
        const decryptedT6System = await decryptionManagerRef.current!.decrypt(phaseCResult.task6System!);
        const decryptedT6User = await decryptionManagerRef.current!.decrypt(phaseCResult.task6User!);

        logLocalTaskPayload('Task 6 split decrypted input', {
          tier: localCapabilityRef.current?.tier,
          runtime: activeRuntime,
          task6System: decryptedT6System,
          task6User: decryptedT6User,
        });
        assertNonEmptyLocalPrompt('Task 6 system', decryptedT6System);
        assertNonEmptyLocalPrompt('Task 6 user', decryptedT6User);

        task6Result = qwen3Manager
          ? await qwen3Manager.executeTask6(decryptedT6System, decryptedT6User)
          : await webllmProcessor!.executeTask6(decryptedT6System, decryptedT6User);
      } else {
        // Mobile Path
        const decryptedT6Prompt = await decryptionManagerRef.current!.decrypt(phaseCResult.task6Prompt!);

        logLocalTaskPayload('Task 6 mobile decrypted input', {
          tier: localCapabilityRef.current?.tier,
          runtime: activeRuntime,
          task6Prompt: decryptedT6Prompt,
        });
        assertNonEmptyLocalPrompt('Task 6', decryptedT6Prompt);

        task6Result = qwen3Manager
          ? await qwen3Manager.executeTask6(decryptedT6Prompt, '')
          : await webllmProcessor!.executeTask6(decryptedT6Prompt);
      }

      const task6Decision = parseTask6Decision(task6Result);
      console.log('🏷️ Task 6 structured decision:', task6Decision);

      if (
        task6Decision.decision === 'AUTO_RESPOND_WITH_CONTEXT' &&
        task6Context?.answer_analysis?.answer_contains_personal_context_fact
      ) {
        console.log('⛔ Task 6 AUTO_RESPOND blocked: backend evidence says final response already used personal context.', {
          task6Decision,
          matchedEvidence: task6Context.answer_analysis.matched_evidence_tokens,
        });
        return;
      }

      if (task6Decision.decision !== 'AUTO_RESPOND_WITH_CONTEXT') {
        console.log(`✅ Task 6 stopped with decision=${task6Decision.decision}. No automatic re-loop needed.`);
        return;
      }

      // Conservative threshold: false positives are worse than false negatives here,
      // because a false positive creates an overbearing autonomous chat turn.
      if (task6Decision.confidence < 0.70) {
        console.log('🟡 Task 6 wanted AUTO_RESPOND_WITH_CONTEXT but confidence was too low. Waiting for user.', task6Decision);
        return;
      }

      const missingInfoQuery =
        task6Decision.retrieval_query.trim() ||
        task6Decision.requested_info.trim();

      if (!missingInfoQuery) {
        console.log('🟡 Task 6 wanted AUTO_RESPOND_WITH_CONTEXT but gave no retrieval target. Waiting for user.');
        return;
      }

      // ── FOLLOW-UP DETECTED: create a NEW visible turn, then re-execute the pipeline ──
      console.log(`🔁 Follow-up detected! Starting background re-loop (iteration ${loopIteration + 1})...`);

      const originalResponse = phaseCResult.completeData?.original_response
        || phaseCResult.completeData?.final_enhanced_html
        || '';

      // ── CIRCUIT BREAKER 1: Tool FAILURE ──
      // The backend post-stream dispatcher writes one of these patterns whenever a tool
      // (file generation, email send, file edit, schedule, etc.) fails. Retrying a
      // deterministic backend failure will deterministically fail again, wasting tokens
      // and stacking jarring duplicate failure bubbles in the UI. The user must intervene.
      const FAILURE_PATTERN = /(❌|\*\*\s*Failed\b|\bFailed to\b|\bFailed:\b)/i;
      if (FAILURE_PATTERN.test(originalResponse)) {
        console.log('⛔ Tool failure detected in response. Cancelling automatic re-loop.');
        return;
      }

      // ── CIRCUIT BREAKER 2: Tool SUCCESS ──
      // When a tool produces a tangible artifact (a file attachment, a sent email, a
      // scheduled task, an edited file, etc.), the "answer" IS the action — not the
      // accompanying meta-text. Task 6 has no visibility into the file_attachment SSE
      // event and often misclassifies these turns as FOLLOW_UP because the visible
      // sentence is just "Here is your file" or "I'll create that for you". We must not
      // re-execute a deterministic action that already completed successfully.
      const SUCCESS_PATTERN = /(✅|🛑|\bSuccessfully\b|\bSuccessful!\b|\bStarted!\b|\bScheduled!\b|\bCancelled!\b|\bUpdated!\b|\bQueued!\b|\bCreation Queued\b|\bOrganization Queued\b)/i;
      const hasFileAttachment = !!phaseCResult.fileAttachment;
      if (hasFileAttachment || SUCCESS_PATTERN.test(originalResponse)) {
        console.log('🟢 Tool success detected (file attachment or success marker). Cancelling automatic re-loop.');
        return;
      }

      // ── LEGACY CIRCUIT BREAKER 3: Plan-like responses ──
      // Do NOT block plain question marks anymore. The structured Task 6 decision
      // now distinguishes human-choice questions from retrievable private-context
      // questions. Keep only the plan-pattern guard as a temporary safety net.
      const PLAN_PATTERN = /^(I'll|I'm going to|Let me|First I'll)/i;
      if (PLAN_PATTERN.test(originalResponse) && !hasFileAttachment) {
        console.log('🟡 Assistant is stating a plan. Cancelling automatic re-loop to wait for user.');
        return;
      }

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = originalResponse;
      const responseText = (tempDiv.textContent || tempDiv.innerText || '').trim();

      if (!responseText) {
        console.warn('⚠️ Task 6 returned FOLLOW_UP, but no assistant text was available for the re-loop query.');
        return;
      }

      const originalUserText = [...basePayload.messages]
        .slice()
        .reverse()
        .find((m: any) => m.role === 'user')?.content?.trim() || '';

      // Use Qwen3.5's extracted missing-information query, not the original broad
      // user message. This is the key fix that makes the reloop search for what the
      // external LLM actually requested.
      const reloopQuery = missingInfoQuery || originalUserText || responseText;

      const followUpResponseId = crypto.randomUUID();
      activeResponseIdRef.current = followUpResponseId;

      const task6LoadingText = task6Decision.requested_info
        ? `Checking your context for: ${task6Decision.requested_info.slice(0, 90)}...`
        : 'Checking whether your context answers that follow-up...';

      setChatBotMessages(prev => [
        ...prev,
        {
          isBotResponse: true,
          responseId: followUpResponseId,
          messageHtml: '',
          isLoading: true,
          loadingText: task6LoadingText,
          timeStamp: formatTime(new Date()),
        },
      ]);

      const reloopMessages = [
        ...basePayload.messages,
        { role: 'assistant', content: responseText },
      ];

      const reloopPayload = {
        ...basePayload,
        messages: reloopMessages,
        
        // New visible turn = new response cycle
        response_cycle_id: followUpResponseId,
        response_phase: 'phase0',
        persist_user_turn: false,
        persist_assistant_turn: false,

        enable_query_expansion: true,
        enable_enhanced_prompting: true,
        llm_response_text: null,
        task4_result_text: null,
        task5_result_text: null,
        is_task6_reloop: true,
        reloop_ai_question: reloopQuery,
        task6_loop_iteration: loopIteration + 1,
        task6_previous_decision: task6Decision,
      };

      if (task6AbortRef.current?.signal.aborted) return;

      const reloopResult = await executePipeline(sseEndpoint, reloopPayload, followUpResponseId);

      if (reloopResult) {
        await runTask6AndMaybeReloop(
          reloopResult,
          sseEndpoint,
          reloopPayload,
          followUpResponseId,
          loopIteration + 1,
        );
      }

    } catch (err) {
      console.error('❌ Task 6 / re-loop error:', err);
      console.error('❌ Task 6 error details:', {
        errorMessage: err instanceof Error ? err.message : String(err),
        errorStack: err instanceof Error ? err.stack : undefined,
      });
    }
  }

  // ================================
  // File upload
  // ================================
  const openFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const gradientIconStyle = {
    fontSize: "1.5rem",
    background:
      "radial-gradient(100% 100% at 100% 0, #6ba0eb 0%, #4a9d6f 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };

  // Handles both file selection from the button and drag-and-drop
  const handleFileSelect = (files: FileList | null) => {
    if (files) {
      uploadFiles(Array.from(files));
    }
  };

  /**
   * Processes an image file before upload:
   * 1. Strips EXIF metadata (privacy: removes GPS, camera info, etc.)
   * 2. Resizes to max 2048px on longest edge (LLM API best practice)
   * 3. Compresses to target ~1MB max file size
   * 4. Converts HEIC/HEIF to JPEG (for cross-browser compatibility)
   *
   * Returns the processed File object, or the original if processing fails.
   */
  const processImageForUpload = async (file: File): Promise<File> => {
    try {
      const options = {
        maxSizeMB: 1,                // Target max file size
        maxWidthOrHeight: 2048,      // Max dimension (matches OpenAI/Claude recommendations)
        useWebWorker: true,          // Non-blocking — runs in a Web Worker
        fileType: 'image/jpeg' as const,  // Convert everything (including HEIC) to JPEG
        initialQuality: 0.85,        // Good quality/size balance
        exifOrientation: undefined,  // This strips EXIF data including GPS
      };

      // Skip processing for small images (under 500KB) and GIFs (to preserve animation)
      if (file.size < 512 * 1024 || file.type === 'image/gif') {
        return file;
      }

      const compressedFile = await imageCompression(file, options);

      // Preserve the original filename but change extension to .jpg if converted
      let newName = file.name;
      if (file.type !== 'image/jpeg' && file.type !== 'image/jpg') {
        const baseName = file.name.replace(/\.[^.]+$/, '');
        newName = `${baseName}.jpg`;
      }

      // Return as a new File object with the correct name
      return new File([compressedFile], newName, {
        type: compressedFile.type,
        lastModified: Date.now(),
      });
    } catch (error) {
      console.warn('Image processing failed, uploading original:', error);
      return file; // Graceful fallback — never block the upload
    }
  };

  // Helper: Check if a file is an image
  const isImageFile = (file: File): boolean => {
    return file.type.startsWith('image/') || 
      /\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)$/i.test(file.name);
  };

  const MAX_IMAGE_SIZE_MB = 20;  // Max size before compression (raw from camera)
  const MAX_FILE_SIZE_MB = 50;   // Max size for non-image files

  // Uploads an array of files, updating state for each one individually
  const uploadFiles = async (filesToUpload: File[]) => {
    // Identity is backend/session-managed. Do not read auth0_sub or CnameID from document.cookie.

    // 1. Validate file sizes upfront
    const validFiles: File[] = [];
    const rejectedNames: string[] = [];

    for (const file of filesToUpload) {
      const maxMB = isImageFile(file) ? MAX_IMAGE_SIZE_MB : MAX_FILE_SIZE_MB;
      if (file.size > maxMB * 1024 * 1024) {
        rejectedNames.push(`${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`);
      } else {
        validFiles.push(file);
      }
    }

    // 2. Alert the user if any files were too large
    if (rejectedNames.length > 0) {
      alert(`The following files exceed the size limit and were not uploaded:\n${rejectedNames.join('\n')}`);
    }

    // 3. Loop over validFiles (NOT filesToUpload)
    for (let file of validFiles) {
      const tempId = Date.now() + Math.random();

      // Generate a local preview URL for images
      let previewUrl: string | undefined;
      if (isImageFile(file)) {
        previewUrl = URL.createObjectURL(file);
      }

      // Add file to state with 'uploading' status
      setUploadedFiles(prev => [
        ...prev,
        {
          id: tempId,
          name: file.name,
          size: file.size,
          type: file.type || file.name.split('.').pop() || 'file',
          status: 'uploading',
          previewUrl,
        },
      ]);

      try {
        // Process images before upload (compress, resize, strip EXIF)
        if (isImageFile(file)) {
          file = await processImageForUpload(file);
        }
  
        const formData = new FormData();
        formData.append("file", file);
        formData.append("tool_id", String(TOOL_ID));
  
        const resp = await axios.post(`${API_SERVER_URL}/upload-file`, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
  
        const newUploadID = resp.data?.uploadID;
        if (newUploadID) {
          setUploadedFiles(prev => prev.map(f =>
            f.id === tempId ? { ...f, status: 'success', id: newUploadID } : f
          ));
        } else {
          throw new Error("Server did not return an uploadID.");
        }
      } catch (err) {
        console.error("Upload error:", err);
        setUploadedFiles(prev => prev.map(f =>
          f.id === tempId ? { ...f, status: 'error' } : f
        ));
      }
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  // Removes a specific file from the preview list by its unique ID
  const handleRemoveFile = (idToRemove: number) => {
    setUploadedFiles(prev => {
      const fileToRemove = prev.find(f => f.id === idToRemove);
      // NEW: Revoke the object URL to free memory
      if (fileToRemove?.previewUrl) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      return prev.filter(file => file.id !== idToRemove);
    });
  };

  // --- Clipboard Paste Handler ---
  const [showPasteIndicator, setShowPasteIndicator] = useState(false);

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            // Generate a meaningful filename for pasted images
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const ext = file.type.split('/')[1] || 'png';
            const namedFile = new File(
              [file],
              `pasted-image-${timestamp}.${ext}`,
              { type: file.type, lastModified: Date.now() }
            );
            imageFiles.push(namedFile);
          }
        }
      }

      if (imageFiles.length > 0) {
        e.preventDefault(); // Prevent the image from being pasted as text
        uploadFiles(imageFiles);

        // Show a brief visual indicator
        setShowPasteIndicator(true);
        setTimeout(() => setShowPasteIndicator(false), 1500);
      }
      // If no images found, let the default paste behavior handle text
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [uploadFiles]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && lightboxUrl) {
        setLightboxUrl(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxUrl]);
  
  // --- Drag and Drop Handlers --- 
  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const allowedFiles = droppedFiles.filter(file => {
        const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
        return dragDropAllowedExtensions.includes(fileExtension);
      });
      
      if (allowedFiles.length !== droppedFiles.length) {
        alert(`Some files were not allowed. Please upload one of the following types: ${dragDropAllowedExtensions.join(", ")}`)
      }

      if (allowedFiles.length > 0) {
        uploadFiles(allowedFiles);
      }
      e.dataTransfer.clearData();
    }
  }, [dragDropAllowedExtensions]);

  useEffect(() => {
    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);
    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  
  // ================================
  // Helpers
  // ================================
  function formatTime(date: Date) {
    let hours = date.getHours();
    let minutes = date.getMinutes();
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    hours = hours || 12; // the hour '0' should be '12'
    const minutesString = minutes < 10 ? "0" + minutes : String(minutes);
    return hours + ":" + minutesString + " " + ampm;
  }

  const formatArchiveDate = (dateStr: string | number | Date) => {
    const date = new Date(dateStr);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  // ---------------------------------------------
  // Voice Mode (Dictation)
  // ---------------------------------------------
  /**
   * Detects the best audio MIME type supported by the current browser.
   * Priority order ensures Safari/iOS compatibility.
   */
  const getSupportedAudioMimeType = (): string => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/wav",
    ];
    for (const type of candidates) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return "audio/webm"; // Fallback
  };

  /**
   * Maps a MIME type to the correct file extension for the backend.
   */
  const getAudioFileExtension = (mimeType: string): string => {
    if (mimeType.includes("mp4")) return "m4a";
    if (mimeType.includes("wav")) return "wav";
    return "webm"; // Default for webm and webm;codecs=opus
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
  
      streamRef.current = stream;

      // TEMPORARY DEBUG
      const audioTrack = stream.getAudioTracks()[0];
      console.log("Audio track settings:", JSON.stringify(audioTrack.getSettings()));

      const mimeType = getSupportedAudioMimeType();
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
  
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
  
      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
  
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        // Release microphone
        streamRef.current?.getTracks().forEach((track) => track.stop());
      };
  
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
  
      // Live duration counter with max recording limit
      const MAX_RECORDING_SECONDS = 300; // 5 minutes
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => {
          if (prev + 1 >= MAX_RECORDING_SECONDS) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error: any) {
      console.error("Microphone access error:", error);
      if (error.name === "NotAllowedError") {
        // User denied permission — show a toast or inline message
        alert("Microphone permission is required for voice input.");
      } else if (error.name === "NotFoundError") {
        alert("No microphone detected on this device.");
      }
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };  
  
  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      // Remove the onstop handler so the blob is NOT saved
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    // Release microphone
    streamRef.current?.getTracks().forEach((track) => track.stop());
    setIsRecording(false);
    setAudioBlob(null);
    setRecordingDuration(0);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };
  
  const removeAudioBlob = () => {
    setAudioBlob(null);
  };

  const transcribeAudio = async (blob: Blob): Promise<string | null> => {
    console.log("Audio blob size:", blob.size, "bytes, type:", blob.type);
    setIsTranscribing(true);
  
    const formData = new FormData();
    const mimeType = blob.type || "audio/webm";
    const extension = getAudioFileExtension(mimeType);
    formData.append("audio", blob, `recording.${extension}`);
  
    try {
      const response = await fetch(`${API_SERVER_URL}/transcribe-audio`, {
        method: "POST",
        body: formData,
        // Note: Do NOT set Content-Type header — the browser sets it
        // automatically with the correct multipart boundary
      });
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Transcription failed");
      }
  
      const data = await response.json();
      const transcribedText = data.text?.trim();
  
      if (transcribedText) {
        // Append to existing input (user may have typed something already)
        setChatInput((prev) =>
          prev.trim() ? `${prev.trim()} ${transcribedText}` : transcribedText
        );
      }
  
      // Clear the audio blob — it has been converted to text
      setAudioBlob(null);
      return transcribedText;
    } catch (error) {
      console.error("Transcription error:", error);
      // Optionally show a toast notification here
      return null;
    } finally {
      setIsTranscribing(false);
    }
  };

  const formatDuration = (totalSeconds: number): string => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };
  
  useEffect(() => {
    if (audioBlob && !isRecording && !isTranscribing) {
      transcribeAudio(audioBlob);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioBlob, isRecording]);

  // ================================
  // Render
  // ================================
  return (
    /**
     *   ### KEY CHANGE ###
     *   Wrap everything in a container with maxWidth so the chat bar
     *   and the messages area are the same narrower width.
     */
    <div
      className={`chatbot-container ${className}`}
      style={{ maxWidth: "900px", margin: "0 auto" }}
    >
      {isDragging && (
        <div className="drag-drop-overlay">
          <div className="drag-drop-content">
            <FaFile className="drag-drop-icon" />
            <p>Drop file or image to upload</p>
          </div>
        </div>
      )}

      {/* NEW: Paste indicator flash */}
      {showPasteIndicator && (
        <div className="paste-indicator">
          Image pasted — uploading...
        </div>
      )}
      
      {/* NEW: Task Notifications Container */}
      <div className="task-notification-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`task-notification-toast ${notif.isHiding ? 'hiding' : ''}`}>
            <div className="task-notification-header">
              <h4 className="task-notification-title">
                <span className="status-dots"><span></span><span></span><span></span></span>
                {notif.title}
              </h4>
              <button className="task-notification-close" onClick={() => removeNotification(notif.id)}>
                <FaTimes />
              </button>
            </div>
            <p className="task-notification-body">{notif.message}</p>
            {notif.conversationId && (
              <button 
                className="task-notification-action"
                onClick={() => {
                  loadArchive(notif.conversationId!);
                  removeNotification(notif.id);
                }}
              >
                View Results
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="mx-auto">
        {/* Archives panel */}
        {showArchives && (
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Archived Conversations</h5>
              <button
                onClick={() => setShowArchives(false)}
                className="btn-close"
                aria-label="Close"
              ></button>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {archivedConversations.map((conv) => (
                  <div key={conv.ConversationID} className="col-md-4">
                    <div
                      className="card h-100 archive-card"
                      onClick={() => loadArchive(conv.ConversationID)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="card-body">
                        <h6 className="card-title">
                          Conversation #{conv.ConversationID}
                        </h6>
                        <p className="card-text small text-muted">
                          {formatArchiveDate(conv.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Top Bar: Back Button + Title + Info */}
        <div className="gradient-underline py-2 position-relative">
          <div className="d-flex align-items-center justify-content-center mt-2 px-3">
            <button
              className="btn btn-outline-primary d-flex align-items-center justify-content-center position-absolute start-0 ms-3 enlive-button ToolHeaderBackBtn"
              onClick={() => setSelectedTool(null)}
            >
              <FaArrowLeft style={{ fontSize: "0.9rem" }} />
            </button>
            <ArchiveSidebar
              ref={archiveSidebarRef}
              toolId={TOOL_ID}
              openArchives={openArchives}
              loadArchive={loadArchive}
            />

            <div className="d-flex align-items-center">
              <span>{title}</span>
            </div>
            <div className="ms-2">
              <InfoSidebar isDark={isDark} />
            </div>
          </div>
        </div>

        {/* Message list */}
        <div
          ref={messageListRef}
          className="d-flex flex-column-reverse px-3"
        >
          <div>
            {chatBotMessages.map((message, index) => (
              <MessageComponent
              key={`${title}-message-${index}`}
              isBotResponse={message.isBotResponse}
              messageHtml={message.messageHtml}
              timeStamp={message.timeStamp}
              attachments={message.attachments}                       // ← FIXED: plural to match ChatBotMessage props
              botAttachments={message.botAttachments}
              isLoading={message.isLoading}
              loadingText={message.loadingText}
              onImageClick={(url: string) => setLightboxUrl(url)}
              computerUseConfirm={message.computerUseConfirm}         // ← NEW: forward the confirmation card data
              sensitiveActionConfirm={message.sensitiveActionConfirm} // ← NEW: forward the sensitive action card data
              recipientDisambiguation={message.recipientDisambiguation} // ← NEW: forward recipient picker data
              errorMessage={message.errorMessage}                     // ← NEW: forward error message
              deepAgentCard={message.deepAgentCard}                   // ← NEW: forward Deep Agent card
            />            
            ))}
          </div>
        </div>
      </div>
      
      {/* Bottom Chat Bar */}
      <div
        ref={inputAreaRef}
        className="fixed-input-area d-flex justify-content-center"
      >
        <div
          className="chat-input-bg rounded-top"
          style={{ maxWidth: "900px", width: "100%", margin: "0 auto", position: "relative" }}
        >
          {/* File preview chips and/or Audio preview chip */}
          {(uploadedFiles.length > 0 || audioBlob) && (
            <div className="file-preview-container">
              {uploadedFiles.map((file) => (
                <FilePreviewChip
                  key={file.id}
                  fileName={file.name}
                  fileSize={file.size}
                  fileType={file.type}
                  isUploading={file.status === 'uploading'}
                  hasError={file.status === 'error'}
                  onRemove={() => handleRemoveFile(file.id)}
                  onImageClick={(url) => setLightboxUrl(url)}  // ← NEW
                />
              ))}

              {/* Audio preview chip */}
              {audioBlob && (
                <AudioPreviewChip
                  audioBlob={audioBlob}
                  onRemove={removeAudioBlob}
                  isTranscribing={isTranscribing}
                />
              )}
            </div>
          )}

          <div className="d-flex mx-3 pt-4 pb-2">
            {/* Paperclip Button + Hidden File Input */}
            <button
              className="upload-icon"
              onClick={openFileSelector}
              title="Upload a file or image"
              aria-label="Upload a file or image"  // ← NEW
            >
              <FaPaperclip style={{ color: "#4a9d6f", fontSize: "1.3rem" }} />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => handleFileSelect(e.target.files)}
              accept={fileAccept}
              multiple
            />

            {/* NEW: Hidden camera capture input (mobile only) */}
            <input
              type="file"
              ref={cameraInputRef}
              style={{ display: "none" }}
              onChange={(e) => handleFileSelect(e.target.files)}
              accept="image/*"
              capture="environment"
            />

            {!isRecording && !audioBlob && (
              <button
                className="upload-icon mic-button"
                onClick={startRecording}
                title="Voice input"
                aria-label="Start voice recording"
              >
                <FaMicrophone size={18} />
              </button>
            )}

            {/* The main text area + send button */}
            <div
              style={{ width: "100%", padding: "7px", borderRadius: "10px" }}
            >
              <div style={{ display: "flex", gap: "1px" }}>
                {isRecording ? (
                  /* ── Recording Overlay ── */
                  <div className="voice-recording-bar">
                    <div className="voice-recording-indicator" />
                    <span className="voice-recording-timer">
                      {formatDuration(recordingDuration)}
                    </span>
                    <span className="voice-recording-label">Recording...</span>
                    <button
                      className="voice-cancel-btn"
                      onClick={cancelRecording}
                      aria-label="Cancel recording"
                      title="Cancel recording"
                    >
                      <FaTimes />
                    </button>
                    <button
                      className="voice-stop-btn"
                      onClick={stopRecording}
                      aria-label="Stop recording"
                      title="Stop recording"
                    >
                      <FaStop />
                    </button>
                  </div>
                ) : (
                  /* ── Normal Text Input ── */
                  <>
                    <textarea
                      id={textareaId}
                      className="form-control"
                      style={{ resize: "none" }}
                      rows={2}
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyUp={(e) =>
                        e.key.toLowerCase() === "enter"
                          ? sendChatBotMessage(chatInput)
                          : undefined
                      }
                      placeholder={placeholder}
                    />
                    {waitingForResponse ? (
                      <button
                        className="chatbot-stop-button"
                        onClick={handleStopGeneration}
                        title="Stop generating"
                        aria-label="Stop generating"
                        style={{
                          maxHeight: "60px",
                          width: "42px",
                          height: "42px",
                          background: "#d4d4d4",
                          borderRadius: "50%",
                          border: "none",
                          color: "#ef4444",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "0",
                          cursor: "pointer",
                          transition: "transform 0.2s ease, color 0.2s ease",
                        }}
                      >
                        <FaStop size={16} />
                      </button>
                    ) : (
                      <button
                        className="chatbot-send-button"
                        onClick={() => sendChatBotMessage(chatInput)}
                        title="Send message"
                        aria-label="Send message"
                      >
                        <FaArrowUp size={30} />
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Second row of buttons under the textarea */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "left",
                  marginTop: "10px",
                  gap: "1rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <button
                    onClick={handleNewChat}
                    className="btn enlive-button rounded-pill d-flex align-items-center justify-content-center NewChatBtn"
                  >
                    <FaPlus style={{ fontSize: "0.8rem" }} />
                  </button>
                  <strong className="text-muted">New Chat</strong>
                </div>

                {/* <button
                  onClick={fetchArchives}
                  className="btn rounded-pill d-flex align-items-center ms-2 ArchiveBtn"
                >
                  <FaArchive className="me-2" />
                  <span>Archives</span>
                </button> */}
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={chatOverlay === 1}
                      onChange={toggleOverlay}
                    />
                    <span className="slider" />
                  </label>
                  <strong className="text-muted">Enlive</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Lightbox Overlay */}
      {lightboxUrl && (
        <div
          className="image-lightbox-overlay"
          onClick={() => setLightboxUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged generated image preview"
          tabIndex={-1}
        >
          <div
            className="image-lightbox-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="image-lightbox-toolbar">
              <span className="image-lightbox-title">Generated image preview</span>
              <div className="image-lightbox-actions">
                <a
                  className="image-lightbox-download"
                  href={lightboxUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open / Save
                </a>
                <button
                  className="image-lightbox-close"
                  onClick={() => setLightboxUrl(null)}
                  aria-label="Close image preview"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            <img src={lightboxUrl} alt="Enlarged generated preview" />
          </div>
        </div>
      )}

      {/* NEW: Token Limit Modal */}
      {showTokenLimitModal && (
        <div className="token-limit-modal-overlay">
          <div className="token-limit-modal-content">
            <button 
              className="token-limit-modal-close" 
              onClick={() => setShowTokenLimitModal(false)}
              aria-label="Close token limit message"
            >
              <FaTimes />
            </button>
            <div className="token-limit-modal-body">
              <p>You have hit your daily token limit, please come back tomorrow!</p>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Permission Denied Modal */}
      {permissionDeniedMsg && (
        <div className="permission-modal-overlay">
          <div className="permission-modal-content">
            <button 
              className="permission-modal-close" 
              onClick={() => setPermissionDeniedMsg(null)}
              aria-label="Close permission message"
            >
              <FaTimes />
            </button>
            <div className="permission-modal-body">
              <p>
                You need to give permission to <strong>{permissionDeniedMsg}</strong> on the My Profile page by turning on the desired setting, clicking "Save", and following the instructions.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Credential Request Modal */}
      {credentialModalState?.isOpen && (
        <div
          className="credential-modal-overlay"
          onClick={handleCredentialModalCancel}
          role="dialog"
          aria-modal="true"
          aria-labelledby="credential-modal-title"
        >
          <div
            className="credential-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="credential-modal-close"
              onClick={handleCredentialModalCancel}
              aria-label="Close credential modal"
            >
              <FaTimes />
            </button>

            <div className="credential-modal-header">
              <div className="credential-modal-eyebrow">Secure Connection</div>
              <h2 id="credential-modal-title" className="credential-modal-title">
                Connect {credentialModalState.providerLabel}
              </h2>
              <p className="credential-modal-subtitle">
                {credentialModalState.description}
              </p>
            </div>

            <div className="credential-modal-body">
              {credentialModalState.oauth_connect_url && (
                <div className="credential-modal-provider-card">
                  <p className="credential-modal-provider-name">Connect with GitHub</p>
                  <p className="credential-modal-provider-copy">
                    Authorize Enlive on GitHub and choose the repositories this chat may access.
                    You can still use a fine-grained personal access token below if needed.
                  </p>
                  <button
                    type="button"
                    className="credential-modal-btn credential-modal-btn-primary"
                    disabled={isSubmittingCredentials}
                    onClick={() => {
                      window.open(
                        credentialModalState.oauth_connect_url,
                        'enlive-github-connect',
                        'width=720,height=820'
                      );
                    }}
                  >
                    Connect GitHub
                  </button>
                </div>
              )}

              <div className="credential-modal-provider-card">
                <p className="credential-modal-provider-name">
                  {credentialModalState.oauth_connect_url
                    ? 'Use a fine-grained personal access token instead'
                    : credentialModalState.providerLabel}
                </p>
                <p className="credential-modal-provider-copy">
                  {credentialModalState.oauth_connect_url
                    ? 'If you prefer not to install the GitHub App, enter a fine-grained GitHub personal access token below. These values should be submitted through this secure form, not through the normal chat bar.'
                    : 'Enter the requested credentials below. These values should be submitted through this secure form, not through the normal chat bar.'}
                </p>
              </div>

              <div className="credential-modal-form">
                {credentialModalState.fields.map((field) => (
                  <div className="credential-modal-field" key={field.key}>
                    <div className="credential-modal-label-row">
                      <label className="credential-modal-label" htmlFor={`cred-${field.key}`}>
                        {field.label}
                        {field.required && <span className="credential-modal-required">*</span>}
                      </label>
                      {!field.required && (
                        <span className="credential-modal-optional">Optional</span>
                      )}
                    </div>

                    {field.type === 'textarea' ? (
                      <textarea
                        id={`cred-${field.key}`}
                        className="credential-modal-textarea"
                        value={credentialFormValues[field.key] || ''}
                        onChange={(e) => handleCredentialFieldChange(field.key, e.target.value)}
                        placeholder={field.placeholder || ''}
                      />
                    ) : (
                      <input
                        id={`cred-${field.key}`}
                        className="credential-modal-input"
                        type={
                          field.type === 'password'
                            ? 'password'
                            : field.type === 'url'
                              ? 'url'
                              : 'text'
                        }
                        value={credentialFormValues[field.key] || ''}
                        onChange={(e) => handleCredentialFieldChange(field.key, e.target.value)}
                        placeholder={field.placeholder || ''}
                        autoComplete="off"
                      />
                    )}

                    {field.helpText && (
                      <div className="credential-modal-help">{field.helpText}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="credential-modal-footer">
              <div className="credential-modal-security-note">
                Credentials entered here are handled separately from the normal chat message flow.
              </div>
              <div className="credential-modal-actions">
                <button
                  type="button"
                  className="credential-modal-btn credential-modal-btn-secondary"
                  onClick={handleCredentialModalCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="credential-modal-btn credential-modal-btn-primary"
                  onClick={handleCredentialModalConfirm}
                  disabled={isSubmittingCredentials}
                >
                  {isSubmittingCredentials ? 'Submitting...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}